'use client';

// Drawer-компоненты «Аккаунтов»: «+ Подключить» (каталог трёх групп с
// /api/cabinet/accounts/catalog → форма из схемы кред-типа) и карточка аккаунта:
// та же форма с правкой полей + участие в сценариях + удаление с проверкой.
// reload передаётся из списка — после мутации ревалидирует общий стор 'cabinet:accounts'.
// OAuth-сервисы (аудит Ф37.2): вход завершается прямо из кабинета — форма показывает
// адрес возврата, после «Подключить и войти» открывается окно провайдера, токен ловит
// и хранит сервер клиента (штатный колбэк n8n), кабинет опрашивает статус входа.
import { webOnly, readPhoneLook, usePhoneLook } from './layout-mode';
import { HERO_ICONS, openHeroAsk } from './hero-ask-sheet';
import { askConfirm } from './ask';
import { ChannelIcon, channelHex } from './bot-templates';
import { useEffect, useMemo, useRef, useState } from 'react';
import { closeNativeSettingsPage, replaceNativeSettingsPages } from '@/lib/account-native';
import { noteFeed } from '@/lib/native-feed-watch';
import type { IgQueueInfo } from '@/lib/ig-queue-core';
import { useLocale, useTranslations } from 'next-intl';
import { refreshPersistent, usePersistentData } from '@/lib/use-persistent-data';
import { warmTarget } from '@/lib/warm';
import { StarButton } from './star-button';
import { useDrawer } from './drawer';
import { openConnectWindow, openSignInWindow, SIGN_IN_DEADLINE_MS, SIGN_IN_POLL_MS, useConnectVia } from './use-connect-via';
import { useSlidePanel, useSlideStack } from './native-layer-host';
import { DActions, DError, DFine, DGroup, DHero, DHint, DList, DWarn } from './drawer-kit';
// Пузыри и поля форм подключения — ТОЛЬКО из набора form-kit (владелец 2026-08-11):
// корень FPane, пузырь с обязательным названием FBlock, поле внутри пузыря FIn,
// выбор из коротких списков FChoiceBlock, тумблер KSwitch строкой. Своих `card fld-card`
// и старых DField в этом файле больше нет. Канон: UI-TEMPLATE-CANON «Элементы формы».
import { FBlock, FChoiceBlock, FHint, FIn, FPane, KSwitch } from './form-kit';
import { MiniAppLinkField } from './mini-app-card-kit';
import { Sk } from './skeleton';
import { fallbackServiceName, PICKER_GROUPS, serviceFor, svcName } from './connection-catalog';
import { ServiceLogo, useNativeServiceLogos, type NativeServiceLogo } from './service-logo';
// ФОТО АККАУНТА ДЛЯ ГЕРОЯ КАРТОЧКИ — ТЕМ ЖЕ КОНВЕЙЕРОМ, что лица «Сообщений» и «Чатов»
// (imgSquarePng внутри): второго пути картинок для натива в кабинете нет.
import { useNativePhotos } from './native-icon';
import { nativeAnaPageSupported } from '@/lib/ana-native';
import { openNativeAccountStats } from './account-stats-native';
import { TelegramBotStats, type StripAccount } from './accounts-strip';
import { InstagramStats } from './instagram-stats';
import { KButton, KInput, KListGroup, KListRow, KListSection } from '@scriptera/ui/kit';
import { useServiceGuide } from './service-guides';
import { missingRequiredText } from './connection-errors';
import { isSecretCredField } from '@/lib/cred-secrets';
import { INTEGRATIONS_TG_IG_ONLY, SETTINGS_PRODUCTS_PEOPLE_HIDDEN, TG_IG_CRED_TYPES } from '@/lib/hidden-features';
// ВОПРОС «РАССКАЖИТЕ О СЕБЕ» — ОДИН НА ВСЕ ВХОДЫ (ONBOARDING-PLAN О5.2): та же форма, что
// в онбординге, живёт в components/onboarding/niche-step.tsx; здесь она открывается после
// подключения из настроек и стоит на карточке подключения.
import {
  NicheAskSheet,
  NicheAccountFields,
  genderOut,
  genderValue,
  nicheAccountKey,
  nicheDraftOf,
  useNicheDraft,
  useNicheOptions,
  type NicheDraft,
} from '@/components/onboarding/niche-step';
import { useNicheData, type NicheAccount, type NicheChoice } from '@/lib/use-niche-personal';
import { robokassaCredData, robokassaPasswordKeys, robokassaSetupUrls } from '@/lib/robokassa-setup';
import { yooKassaNotifyUrl } from '@/lib/yookassa-setup';
import { stripeNotifyUrl } from '@/lib/stripe-setup';
import { PAY_RECEIPT_KEYS, isCustomServiceCredType, payReceiptOptionsFor } from '@scriptera/ui/scenario';
import { SmartSelect } from '@scriptera/ui';
import { CustomServiceForm, refreshCustomServices } from './custom-service-form';
import { useBillingDrawer, useBillingPlate, useNeedPayConnect } from './billing';
import { hasNativeNotice, showNativeNotice } from './confirm-sheet';
import { EDITOR_SHEET } from '@/lib/sheet-detent';
import { BOT_FATHER_URL, tgTokenHowPayload, withBotFatherLinks } from '@/components/onboarding/tg-token-how';
import {
  nativeBridge,
  readRoleHex,
  useNativeApp,
  useNativeMethod,
  type NativeAccountAction,
  type NativeAccountField,
  type NativeAccountGroup,
  type NativeAccountPage,
} from '@/lib/native-app';

/**
 * ЛИЦО КАНАЛА В ГЕРОЕ КАРТОЧКИ ПОДКЛЮЧЕНИЯ (телефон в браузере, кадры iOS K12-connection-tg /
 * -ig; AccountPageHeroView «портрет» с `photoBadge`): фото аккаунта кругом 88, нет фото — буква
 * логина на пастели; знак сервиса — маленькой плашкой в углу, а не вместо лица. Фото берётся
 * тем же адресом, что у родного героя (`/api/cabinet/accounts/<id>/avatar`); не отдалось —
 * остаётся буква.
 */
function ConnHeroFace({ accountId, initial, badge }: { accountId: string; initial: string; badge: React.ReactNode }) {
  const [photo, setPhoto] = useState(true);
  return (
    <span className="conn-face">
      <span className="conn-face__i" aria-hidden>
        {initial}
      </span>
      {photo ? (
        // eslint-disable-next-line @next/next/no-img-element -- фото аккаунта из нашего бакета
        <img className="conn-face__p" src={`/api/cabinet/accounts/${accountId}/avatar`} alt="" width={88} height={88} decoding="async" onError={() => setPhoto(false)} />
      ) : null}
      <span className="conn-face__b">{badge}</span>
    </span>
  );
}

export interface Account {
  id: string;
  name: string;
  type: string;
  /** Главное отличие подключения (31.07): «@логин» бота, аккаунт Instagram, адрес
   *  портала amoCRM/Битрикс24, идентификатор магазина Робокассы. null — узнать не у
   *  кого (доступ заведён руками в редакторе, его ключа у нас нет). */
  handle?: string | null;
  /** Сценарии, где доступ задействован (по нашим сценариям — для точки статуса). */
  usedBy?: string[];
  /** Вердикт сторожа здоровья (К36): подключение не работает и почему. null — здорово. */
  health?: { code: 'hijacked' | 'expired' | 'revoked' | 'no_events'; at: string | null } | null;
  /** Робокасса, подключённая до мастера: алгоритм подписи не SHA256. Молча его не
   *  меняем (в кассе у клиента остался старый) — карточка предупреждает. */
  legacyHash?: boolean;
  /** ОЧЕРЕДЬ ОТВЕТОВ ИНСТАГРАМА (SCALE-PLAN Б5): сколько ответов с этого аккаунта ждут
   *  отправки, сколько ждать и сколько публичных пропущено за сутки. null — очереди нет
   *  или доступ не инстаграмный. */
  igQueue?: IgQueueInfo | null;
  /** Когда подключено (первая запись сейфа); null — доступ без сейфа. Плитка «Подключений» (П6). */
  connectedAt?: string | null;
  /** Сколько переписок идёт через аккаунт (плитка «Подключений», П6). */
  dialogs?: number;
  /** Тумблер аккаунта «включён / выключен» (ТЗ ACCOUNTS-TOGGLE §3.4); null/нет — доступ не
   *  тарифицируемый (касса, почта…), тумблера нет. */
  switch?: AccountSwitch | null;
}

/** Состояние тумблера аккаунта с сервера (`GET /api/cabinet/accounts`, поле `switch`). */
export interface AccountSwitch {
  key: string;
  state: 'on' | 'off';
  /** `moved` — аккаунт ушёл в другой кабинет (ТЗ ACCOUNTS-TOGGLE §3.7). */
  offReason: 'unpaid' | 'manual' | 'moved' | null;
}

interface JsonSchema {
  properties?: Record<string, { type?: string }>;
  required?: string[];
}

function humanize(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
}

/**
 * Служебная английская заглушка проверки доступа — её клиенту показывать нечего.
 * Такое возвращает сам n8n, когда узел не объяснил причину («failed - please check your
 * credentials», «Authorization failed - please check your credentials»); осмысленные
 * ответы сервисов (в том числе наши русские, вроде «amoCRM не принял токен доступа»)
 * под правило не попадают и показываются как есть.
 */
function isBoilerplateTestError(message: string): boolean {
  return /please check your credentials/i.test(message.trim());
}
// Секретные поля (пароль в форме, «сохранён» при правке) — правило общее с сервером.
const isSecret = isSecretCredField;

const okAccept = (j: unknown) => !!(j as { ok?: boolean })?.ok;

// ─────────── Ключи кэшей раздела (usePersistentData) и предзагрузка ───────────
// Вход в «Подключения» греет эти кэши (connections.tsx), поэтому каталог, форма и
// карточка подключённого открываются мгновенно из кэша, обновление доезжает тихо фоном.

/** Карточка подключённого: участие в сценариях + сохранённые значения формы (без секретов). */
export const accountKey = (id: string) => `cabinet:account:${id}`;
export const accountUrl = (id: string) => `/api/cabinet/accounts/${encodeURIComponent(id)}`;
/** Статус OAuth-входа аккаунта (только для типов через вход в аккаунт). */
const accountOauthKey = (id: string) => `cabinet:account:${id}:oauth`;
const accountOauthUrl = (id: string) => `/api/cabinet/accounts/${encodeURIComponent(id)}/oauth`;
/** Схема полей кред-типа (подписи зависят от языка — язык в ключе). */
const schemaKey = (credType: string, locale: string) => `cabinet:accounts:schema:${credType}:${locale}`;
const schemaUrl = (credType: string, locale: string) =>
  `/api/cabinet/accounts/schema?type=${encodeURIComponent(credType)}&locale=${encodeURIComponent(locale)}`;

/** Прогреть данные одного подключённого: карточка + схема его формы (+ статус входа). */
export function prefetchAccountData(id: string, credType: string, locale: string): void {
  refreshPersistent(accountKey(id), accountUrl(id), { accept: okAccept });
  prefetchCredSchema(credType, locale);
  if (/oauth2/i.test(credType)) refreshPersistent(accountOauthKey(id), accountOauthUrl(id), { accept: okAccept });
}

/** То же для ФОНОВОГО прогрева с родителя («Подключения» → карточки всех аккаунтов, PREFETCH-TZ §3): цель плана
 *  прогрева `connections.cards` (очередь warm.ts). Явное освежение после правки — `prefetchAccountData` выше, мимо
 *  очереди (`refreshPersistent`). */
export function warmAccountData(id: string, credType: string, locale: string): void {
  warmTarget('connections.cards', accountKey(id), accountUrl(id), { accept: okAccept });
  warmTarget('connections.cards', schemaKey(credType, locale), schemaUrl(credType, locale), { accept: okAccept });
  if (/oauth2/i.test(credType)) warmTarget('connections.cards', accountOauthKey(id), accountOauthUrl(id), { accept: okAccept });
}

/** Фоновый прогрев схемы формы кред-типа — цель плана `connections.schemas` (очередь warm.ts). */
export function warmCredSchema(credType: string, locale: string): void {
  warmTarget('connections.schemas', schemaKey(credType, locale), schemaUrl(credType, locale), { accept: okAccept });
}

/** Прогреть схему формы одного кред-типа. Вход в раздел греет схемы ВСЕХ сервисов
 *  каталога (владелец 2026-08-03): раньше грелись только подключённые типы, и «Подключить
 *  Email» у человека без почты открывался скелетоном. Запрос лёгкий, сервисов немного. */
export function prefetchCredSchema(credType: string, locale: string): void {
  refreshPersistent(schemaKey(credType, locale), schemaUrl(credType, locale), { accept: okAccept });
}

// ─────────── OAuth-вход (Ф37.2): окно провайдера + опрос статуса ───────────
// Окно входа, сроки опроса и вход через НАШ домен (useConnectVia) — в общем модуле
// ./use-connect-via: тем же куском живёт экран «добавьте аккаунт» после регистрации.

/**
 * Вход в OAuth-аккаунт: POST отдаёт адрес входа у провайдера (state/PKCE собирает n8n),
 * дальше опрашиваем статус, пока сервер клиента не поймает колбэк и не сохранит токен.
 * onOk зовётся один раз при подтверждённом входе (из тика опроса, не из эффекта).
 */
function useOauthSignIn(onOk: (accountId: string) => void) {
  const [state, setState] = useState<'idle' | 'waiting' | 'ok' | 'fail'>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onOkRef = useRef(onOk);
  useEffect(() => {
    onOkRef.current = onOk;
  }, [onOk]);
  const stop = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  };
  useEffect(() => stop, []);

  const start = async (accountId: string, popup: Window | null) => {
    stop();
    setState('waiting');
    const r = await fetch(`/api/cabinet/accounts/${encodeURIComponent(accountId)}/oauth`, { method: 'POST' })
      .then((res) => res.json())
      .catch(() => ({ ok: false }));
    if (!r.ok || typeof r.url !== 'string') {
      popup?.close();
      setState('fail');
      return;
    }
    if (popup && !popup.closed) popup.location.href = r.url;
    else window.open(r.url, 'scriptera-oauth', 'width=620,height=720');
    const deadline = Date.now() + SIGN_IN_DEADLINE_MS;
    // Цепочка setTimeout, не setInterval: опрос будит спящий сервер и может тянуться —
    // параллельные тики не копим.
    const tick = async () => {
      const s = await fetch(`/api/cabinet/accounts/${encodeURIComponent(accountId)}/oauth`, { cache: 'no-store' })
        .then((res) => res.json())
        .catch(() => null);
      if (s?.ok && s.connected) {
        stop();
        setState('ok');
        onOkRef.current(accountId);
        return;
      }
      if (Date.now() > deadline) {
        stop();
        setState('fail');
        return;
      }
      timerRef.current = setTimeout(tick, SIGN_IN_POLL_MS);
    };
    timerRef.current = setTimeout(tick, SIGN_IN_POLL_MS);
  };

  return { state, start };
}


/**
 * ЛИМИТ АККАУНТОВ — объяснение вместо отказа (владелец 2026-08-10: «скажи, что надо
 * оплатить, дай кнопку и объясни почему»). Текст разный: на бесплатном — что бесплатно
 * держим только два аккаунта; на Pro — что за ещё один надо доплатить.
 */
function AccountLimitNote({ onPay, hero }: { onPay: () => void; hero?: React.ReactNode }) {
  const t = useTranslations('cabinet.connections');
  // В ПРИЛОЖЕНИИ НЕ ПРЕДЛАГАЕМ ОПЛАТУ (CLAUDE.md п.7, ЖЕЛЕЗНОЕ): ни кнопки, ни слова
  // «Pro» с намёком на покупку — только честная причина. Оплата живёт в вебе.
  const native = useNativeApp();
  const { data } = usePersistentData<{ subscription?: { paidAccountsCount?: number } | null }>(
    'cabinet:billing-summary',
    '/api/billing/summary',
    { accept: (j) => !!(j as { ok?: boolean })?.ok },
  );
  const paid = (data?.subscription?.paidAccountsCount ?? 0) > 0;
  if (native) {
    return (
      <DGroup className="iosf">
        <DHint>{t(paid ? 'limitPaidNative' : 'limitFreeNative')}</DHint>
      </DGroup>
    );
  }
  // Лимит на телефоне в браузере (М65): тот же лист со значком и заголовком, абзац серым,
  // кнопка оплаты широкая (в вебе оплата разрешена — кнопка остаётся).
  return (
    <DGroup className="iosf">
      {hero}
      <DHint>{t(paid ? 'limitPaidNote' : 'limitFreeNote')}</DHint>
      <DActions className="cf-acts">
        <KButton tone="primary" {...(hero ? { action: true } : {})} onClick={onPay}>
          {t(paid ? 'limitPaidAction' : 'limitFreeAction')}
        </KButton>
      </DActions>
    </DGroup>
  );
}

/** Герой листа подключения (телефон, браузер; кадр W32-tg-step3-add-account): значок
 *  сервиса 72 по центру и заголовок «Подключить …» 22 жирным под ним. */
function ConnectHero({ svc, title }: { svc: PickerService; title: string }) {
  return (
    <div className="cf-hero">
      {/* Знак 56 в месте 72 — ScrHero.glyph / ScrHero.box (кадр iOS W32-tg-step3-add-account). */}
      <ServiceLogo credType={svc.credType} {...(svc.iconNode ? { iconNode: svc.iconNode } : {})} name={svc.name} size={56} />
      <h2 className="cf-hero-t">{title}</h2>
    </div>
  );
}

/**
 * ЛИСТ ВВОДА ОДНОГО ПОЛЯ (М66, кадр W32-tg-step3-token): поле-карточка, под ним широкая
 * «Сохранить» — бледная, пока пусто. Курсор сразу в поле. Значение уезжает форме по
 * «Сохранить»; «×» листа — без изменений.
 */
function ConnFieldSheet({
  initial,
  secret,
  saveLabel,
  onSave,
}: {
  initial: string;
  secret: boolean;
  saveLabel: string;
  onSave: (v: string) => void;
}) {
  const [v, setV] = useState(initial);
  return (
    <FPane>
      <KListGroup>
        <div className="k-lfield cf-field-sheet">
          <KInput
            value={v}
            type={secret ? 'password' : 'text'}
            autoFocus
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            onChange={(e) => setV(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && v.trim()) onSave(v.trim());
            }}
          />
        </div>
      </KListGroup>
      <KButton action tone="primary" className="cf-field-save" disabled={!v.trim()} onClick={() => onSave(v.trim())}>
        {saveLabel}
      </KButton>
    </FPane>
  );
}

// ─────────── «+ Подключить»: поиск по всему каталогу → форма из схемы ───────────
/** Сервис в пикере: кураторский (иконка из SERVICES) или из полного каталога.
 * Экспорт — для карточки-замочка в чате сценария (этап 4), где форма та же. */
export interface PickerService {
  credType: string;
  name: string;
  oauth?: boolean;
  generic?: boolean;
  /** Ключ проверяется при подключении (credential test n8n). */
  canTest?: boolean;
  /** Узел-представитель — по нему цветной логотип из N8N_ICONS (как в пикере шага). */
  iconNode?: string;
  /** «Скоро» (владелец 2026-08-03): плитка видна, но подключение пока не открыто. */
  soon?: boolean;
}

interface CatalogEntry {
  credType: string;
  displayName: string;
  popular?: boolean;
  generic?: boolean;
  canTest?: boolean;
  /** Бизнес-категории (id из node_categories) — от нод сервиса. */
  cats?: number[];
  iconNode?: string;
}

interface CatalogCategory {
  id: number;
  slug: string;
  nameRu: string;
  nameEn: string;
}

export interface CatalogData {
  ok: boolean;
  services: CatalogEntry[];
  categories?: CatalogCategory[];
}

// Порядок секций-категорий пикера: сначала то, что нужно обычному бизнесу, техника — вниз.
const CAT_ORDER = ['communication', 'sales', 'marketing', 'finance', 'data', 'productivity', 'ai', 'analytics', 'core', 'development', 'utility', 'misc', 'devtools'];

// Русские подписи универсальных типов «своего сервиса» (i18n-ключи cabinet.connections.*).
const GENERIC_LABEL_KEYS: Record<string, string> = {
  httpHeaderAuth: 'genericHeaderAuth',
  httpBasicAuth: 'genericBasicAuth',
  httpQueryAuth: 'genericQueryAuth',
  oAuth2Api: 'genericOAuth2',
};

function toPickerService(entry: CatalogEntry, locale: string, genericLabel?: string): PickerService {
  return {
    credType: entry.credType,
    // Имя — на языке интерфейса (владелец 2026-08-06, Ш4): у кураторских сервисов есть
    // английский двойник; чего нет в кураторском списке — как прислал каталог узлов.
    name: genericLabel ?? svcName(serviceFor(entry.credType), locale) ?? entry.displayName,
    oauth: serviceFor(entry.credType)?.oauth ?? /oauth2/i.test(entry.credType),
    generic: entry.generic,
    canTest: entry.canTest,
    iconNode: entry.iconNode,
    soon: serviceFor(entry.credType)?.soon,
  };
}

// Значок сервиса (канон-плитка 40) живёт в своём модуле — ./service-logo; реэкспорт
// здесь, чтобы не трогать импорты списка подключений и пикеров.
export { ServiceLogo };

// Звезда переехала в свой модуль (нужна и админке «Избранное»); реэкспорт — чтобы не
// трогать импорты пикеров шагов/триггеров.
export { StarButton };

/** Заголовок группы каталога — пузырь-чип в стиле чипов «Скриптов» (владелец 2026-08-03:
 *  каталог «в том же дизайне, что страница скриптов, полностью»). Класс общий с чипами
 *  каталога шаблонов (.tpl-chip) — близнецов не заводим. */
function PickerCatChip({ title }: { title: string }) {
  return (
    <div style={{ margin: '16px 0 10px' }}>
      <span className="tpl-chip">{title}</span>
    </div>
  );
}

function PickerSection({
  title,
  list,
  openForm,
}: {
  title: string;
  list: PickerService[];
  openForm: (svc: PickerService) => void;
}) {
  const t = useTranslations('cabinet.connections');
  const tSoon = t('soon');
  if (list.length === 0) return null;
  // ПЛИТКИ вместо строк (владелец 2026-08-03): та же сетка и та же карточка, что у
  // «Скриптов» (.scn-grid / .scn-card), внутри — плитка-значок сервиса и название.
  // Звёзд избранного в плитке НЕТ (владелец 2026-08-03: для трёх групп не нужны).
  // B5 (builder-ux): галочки canTest в списке тоже нет — читалась как «уже
  // подключено»; про проверку ключа говорит сама форма (testEnabled/testMissing).
  return (
    <>
      <PickerCatChip title={title} />
      <div className="ai-vgroups scn-vgroups scn-grid">
        {list.map((svc) => (
          <div
            className={`scn-card${svc.soon ? ' scn-card-soon' : ''}`}
            key={svc.credType}
            {...(svc.soon
              ? { 'aria-disabled': true }
              : {
                  role: 'button',
                  tabIndex: 0,
                  onClick: () => openForm(svc),
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') openForm(svc);
                  },
                })}
          >
            <ServiceLogo credType={svc.credType} iconNode={svc.iconNode} name={svc.name} />
            <div className="scn-card-col">
              <div className="scn-card-name">
                {svc.name}
                {/* «Скоро» (владелец 2026-08-03): подключение ещё не открыто — плитка
                    видна, но не кликается; снимается флагом soon в каталоге. */}
                {svc.soon ? <span className="scn-soon">{tSoon}</span> : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/**
 * СЕКЦИИ КАТАЛОГА «ПОДКЛЮЧИТЬ» — ОДНОЙ РУКОЙ (волна 3): ими живут веб-пикер и РОДНАЯ
 * страница каталога. Поиска и звёзд избранного в каталоге НЕТ (владелец 2026-08-03:
 * групп всего три — поиск и избранное им не нужны). Группы «На вашем сайте» тоже нет
 * (владелец 2026-08-03): форме и чат-виджету настраивать нечего — они появляются сами
 * из сценария с таким запуском.
 *
 * Секции — КУРАТОРСКИЕ группы одного смысла (PICKER_GROUPS, владелец 2026-08-03:
 * «Приём оплаты» отдельно от «CRM и данные», прежние категории нод их смешивали).
 * Сервис вне групп — в прежние секции по бизнес-категориям, без категории — в хвост
 * «Все сервисы».
 */
function usePickerSections(data: CatalogData | null | undefined): {
  sections: { title: string; list: PickerService[] }[];
} {
  const t = useTranslations('cabinet.connections');
  const locale = useLocale();
  return useMemo(() => {
    const all = data?.services ?? [];
    const cats = data?.categories ?? [];
    const catRank = new Map(cats.map((c) => [c.id, CAT_ORDER.indexOf(c.slug) === -1 ? 99 : CAT_ORDER.indexOf(c.slug)]));
    const catName = new Map(cats.map((c) => [c.id, locale === 'en' ? c.nameEn : c.nameRu]));
    const groupOf = new Map<string, number>();
    PICKER_GROUPS.forEach((g, i) => g.types.forEach((type) => groupOf.set(type, i)));
    const byGroup = new Map<number, PickerService[]>();
    const byCat = new Map<number, PickerService[]>();
    const rest: PickerService[] = [];
    for (const entry of all) {
      const labelKey = entry.generic ? GENERIC_LABEL_KEYS[entry.credType] : undefined;
      const svc = toPickerService(entry, locale, labelKey ? t(labelKey) : undefined);
      // Универсальные типы в общем списке не показываем: любой сервис вне каталога
      // подключается плиткой «Свой сервис» внизу (владелец 2026-08-03, два уровня).
      if (entry.generic) continue;
      const grp = groupOf.get(entry.credType);
      if (grp != null) {
        const list = byGroup.get(grp) ?? [];
        list.push(svc);
        byGroup.set(grp, list);
        continue;
      }
      const best = (entry.cats ?? []).reduce<number | null>(
        (acc, id) => (acc == null || (catRank.get(id) ?? 99) < (catRank.get(acc) ?? 99) ? id : acc),
        null,
      );
      if (best == null) rest.push(svc);
      else {
        const list = byCat.get(best) ?? [];
        list.push(svc);
        byCat.set(best, list);
      }
    }
    const sections = PICKER_GROUPS.map((g, i) => ({
      title: t(g.labelKey),
      // Порядок карточек в группе — кураторский (порядок types), не алфавит каталога.
      list: (byGroup.get(i) ?? []).sort((a, b) => g.types.indexOf(a.credType) - g.types.indexOf(b.credType)),
    })).filter((s) => s.list.length > 0);
    sections.push(
      ...[...byCat.entries()]
        .sort((a, b) => (catRank.get(a[0]) ?? 99) - (catRank.get(b[0]) ?? 99))
        .map(([id, list]) => ({ title: catName.get(id) ?? '', list })),
    );
    if (rest.length) sections.push({ title: t('allTitle'), list: rest });
    return { sections };
  }, [data, t, locale]);
}

/**
 * ЕСТЬ ЛИ О ЧЁМ СПРАШИВАТЬ (О5.2). Ниша, тип предложения и пол — свойства аккаунта
 * КАНАЛА (Телеграм и Инстаграм): у кассы, почты и CRM никакого профиля нет, и вопрос
 * там был бы бессмыслицей.
 */
export function nicheAskable(credType: string): boolean {
  return TG_IG_CRED_TYPES.includes(credType);
}

/**
 * ВОПРОС ПОСЛЕ УСПЕШНОГО ПОДКЛЮЧЕНИЯ (веб) — ВЕРТИКАЛЬНОЙ ШТОРКОЙ (О5.2 п.3; словарь
 * видов docs/NATIVE-SLIDE-PLAN.md §11.0: короткий вопрос — вертикальная шторка).
 * Возвращает руку «спросить про только что подключённый доступ»; не канал — молчит.
 */
export function useNicheAsk(): (credType: string, credId: string) => void {
  const t = useTranslations('onboarding.niche');
  const panel = useSlidePanel();
  return (credType, credId) => {
    if (!nicheAskable(credType)) return;
    panel.open({
      title: t('askTitle'),
      sheet: true,
      body: <NicheAskSheet credId={credId} onClose={() => panel.close()} />,
    });
  };
}

/** Аккаунт этого доступа среди подключённых (по нему стоят поля ниши на карточке). */
function nicheAccountOf(accounts: NicheAccount[] | undefined, credId: string): NicheAccount | null {
  return accounts?.find((a) => a.credId === credId) ?? null;
}

/**
 * ТРИ ПОЛЯ НИШИ РОДНЫМИ СТРОКАМИ — существующим видом 'choice' (список с галочкой):
 * своих видов полей для этого не заводим (О5.2, общие правила этапа).
 */
function nicheNativeFields(opts: {
  ns: string;
  acc: NicheAccount;
  draft: NicheDraft;
  lists: { niches: { value: string; label: string }[]; offers: { value: string; label: string }[]; genders: { value: string; label: string }[] };
  t: (key: string) => string;
  /** КАРТОЧКА АККАУНТА (владелец 09.09.2026, волна 2 п.6): строки КОРОТКИЕ — «Тема»,
   *  «Что продаёте», «Пол», — и абзацев под названиями нет: объяснение стоит ОДНОЙ
   *  сноской под пузырём. На странице вопроса «расскажите о себе» всё как было. */
  short?: boolean;
}): NativeAccountField[] {
  const { ns, acc, draft, lists, t, short } = opts;
  const cur = nicheDraftOf(draft, acc);
  const touched = draft[nicheAccountKey(acc)] !== undefined;
  const row = (
    key: string,
    title: string,
    hint: string,
    value: string,
    options: { value: string; label: string }[],
  ): NativeAccountField => ({
    key: `${ns}:${key}`,
    kind: 'choice',
    title,
    hint,
    value: options.find((o) => o.value === value)?.label ?? '',
    picked: value,
    options: options.map((o) => ({ value: o.value, label: o.label })),
  });
  // §11.38 п.1 (владелец 31.10): вид один — короткие заголовки, значение справа целиком, без абзацев в строках;
  // пояснение — подписью под группой (`nicheGroupHint`). Параметр `short` оставлен вызовам.
  void short;
  return [
    row('niche', t('nicheLabelShort'), '', cur.nicheKey ?? '', lists.niches),
    row('offer', t('offerLabel'), '', cur.offerKey ?? '', lists.offers),
    row('gender', t('genderLabelShort'), '', genderValue(cur, touched), lists.genders),
  ];
}

/** Подпись под группой полей ниши: пустой профиль (догадки нет, не выбрано) — «нет данных», иначе — зачем поля. */
function nicheGroupHint(acc: NicheAccount, t: (key: string) => string): string {
  return !acc.chosen && !acc.guess ? t('noSourceHint') : t('groupHint');
}

/** Ответ родного листа выбора → новый выбор человека (иммутабельно). Не наша строка — null. */
function nicheNativeSet(
  ns: string,
  acc: NicheAccount,
  draft: NicheDraft,
  key: string,
  value: string | boolean,
): NicheChoice | null {
  if (typeof value !== 'string') return null;
  const cur = nicheDraftOf(draft, acc);
  if (key === `${ns}:niche`) return { ...cur, nicheKey: value };
  if (key === `${ns}:offer`) return { ...cur, offerKey: value };
  if (key === `${ns}:gender`) return { ...cur, gender: genderOut(value) };
  return null;
}

export function ConnectPicker({
  reload,
  onPickService,
}: {
  reload: () => void;
  /** Вторая колонка десктопа (владелец 2026-08-01): форма подключения открывается не
   *  шторкой, а на месте пикера — правым полем колонки. Задан — шторок не открываем. */
  onPickService?: (credType: string) => void;
}) {
  const t = useTranslations('cabinet.connections');
  const drawer = useDrawer();
  // Приложение: форма подключения — ОТДЕЛЬНЫМ боковым слоем поверх пикера (владелец
  // 2026-07-21); пикер жив под слоем, «назад» (свайп/←) снимает слой.
  const stack = useSlideStack();
  // Заголовок открытой формы — «Подключить <Название>» (владелец 2026-08-03): по одному
  // заголовку понятно, что это подключение сервиса, а не его настройки.
  // Подключили — возвращаемся К СПИСКУ АККАУНТОВ, а не к пикеру «Подключить аккаунт»
  // (владелец 2026-08-10): снимаем оба слоя — форму и сам пикер.
  const backToList = () => {
    if (stack.available) {
      stack.pop();
      stack.pop();
      return;
    }
    drawer.close();
  };
  // Подключили КАНАЛ — сразу спрашиваем про него самого (О5.2 п.3): ниша, что предлагает
  // и пол. Прочим сервисам вопрос не задаётся (профиля у них нет) — рука молчит сама.
  const askNiche = useNicheAsk();
  const afterConnect = (svc: PickerService, id: string) => {
    backToList();
    askNiche(svc.credType, id);
  };
  const openForm = (svc: PickerService) => {
    if (onPickService) {
      onPickService(svc.credType);
      return;
    }
    if (stack.available) {
      stack.push({
        title: t('connectServiceTitle', { name: svc.name }),
        body: <ConnectForm svc={svc} reload={reload} onDone={(id) => afterConnect(svc, id)} />,
      });
      return;
    }
    drawer.open({
      title: t('connectServiceTitle', { name: svc.name }),
      ...webOnly({ sheet: true }), // подключение — вертикальный лист в браузере (ТЗ WEB-LOOK Э2.5)
      body: <ConnectForm svc={svc} reload={reload} onDone={(id) => afterConnect(svc, id)} />,
      onBack: () => drawer.open({ title: t('connectTitle'), ...webOnly({ sheet: true }), body: <ConnectPicker reload={reload} /> }),
    });
  };
  // Каталог общий на площадку — из нашей БД; кэш прогрет входом в раздел
  // (connections.tsx, prefetchPersistent) — сюда он обычно приходит мгновенно.
  const { data, error } = usePersistentData<CatalogData>(
    'cabinet:accounts:catalog',
    '/api/cabinet/accounts/catalog',
    { accept: okAccept },
  );

  const { sections } = usePickerSections(data);

  // Плитка «Свой сервис» (владелец 2026-08-03): открывает СВОЮ форму (описание чужого
  // API: адрес, способ, авторизация, входы, поля ответа), а не форму по схеме кред-типа.
  const openCustom = () => {
    if (onPickService) {
      onPickService('custom');
      return;
    }
    if (stack.available) {
      stack.push({ title: t('customService'), body: <CustomServiceForm reload={reload} /> });
      return;
    }
    drawer.open({
      title: t('customService'),
      ...webOnly({ sheet: true }), // подключение — вертикальный лист в браузере (ТЗ WEB-LOOK Э2.5)
      body: <CustomServiceForm reload={reload} />,
      onBack: () => drawer.open({ title: t('connectTitle'), ...webOnly({ sheet: true }), body: <ConnectPicker reload={reload} /> }),
    });
  };

  // Обычная шторка без своего каркаса (канон 2026-07-24): скроллит штатный .dr-body.
  return (
    <div className="pick-list">
      {!data ? (
        error ? (
          <div className="empty">{t('catalogError')}</div>
        ) : (
          // Скелетон = каркас настоящего каталога (владелец 2026-08-03: «скелетон обязан
          // выглядеть как страница»): чип группы и та же сетка плиток, что у загруженного.
          <div className="sk-hold">
            <div style={{ margin: '16px 0 10px' }}>
              <Sk w={110} h={28} r="r" />
            </div>
            <div className="ai-vgroups scn-vgroups scn-grid">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div className="scn-card" key={i}>
                  <Sk w={40} h={40} r="round" />
                  <div className="scn-card-col">
                    <Sk w={i % 2 ? 100 : 130} h={13} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      ) : (
        <>
          {sections.map((s) => (
            <PickerSection key={s.title} title={s.title} list={s.list} openForm={openForm} />
          ))}
          {/* «Свой сервис» — внизу каталога вместо прежнего блока «Не нашли свой
              сервис?» (владелец 2026-08-03): подключается ЛЮБОЙ сервис — клиент один раз
              описывает запрос, дальше конструктор говорит с ним человеческими именами.
              СЕЙЧАС ПЛИТКИ НЕТ: подключать можно только Телеграм и Инстаграм (владелец
              31.08.2026, lib/hidden-features.ts) — форма и код целы, убран только вход. */}
          {INTEGRATIONS_TG_IG_ONLY ? null : <OwnServiceTile onOpen={openCustom} />}
        </>
      )}
    </div>
  );
}

// Группа «На вашем сайте» («Форма на сайте», «Чат-виджет») из каталога УДАЛЕНА
// (владелец 2026-08-03): настраивать там нечего — оба канала появляются сами из
// сценария с таким запуском. Сами механизмы (форма/чат-виджет) не тронуты.

/**
 * «Не нашли свой сервис?» → плитка «Свой сервис» (владелец 2026-08-03): та же карточка,
 * что у всего каталога, значок — буква-кружок (фирменного логотипа у чужого API нет).
 */
function OwnServiceTile({ onOpen }: { onOpen: () => void }) {
  const t = useTranslations('cabinet.connections');
  return (
    <div style={{ marginTop: 14 }}>
      <div className="nm" style={{ fontSize: 15 }}>{t('notFoundTitle')}</div>
      <p className="meta" style={{ margin: '6px 0 10px' }}>{t('svcTileText')}</p>
      <div className="ai-vgroups scn-vgroups scn-grid">
        <div
          className="scn-card"
          role="button"
          tabIndex={0}
          onClick={onOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') onOpen();
          }}
        >
          <ServiceLogo credType="customService" name={t('customService')} />
          <div className="scn-card-col">
            <div className="scn-card-name">{t('customService')}</div>
            <div className="scn-card-desc">{t('svcTileDesc')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Скелетон формы подключения — КАРКАС настоящего макета (владелец 2026-08-03: «скелетон
 * обязан выглядеть как страница, а не как случайные серые полосы»). Всё, что известно
 * заранее, — настоящим текстом: инструкция «где взять ключ» (гайд не зависит от схемы),
 * подпись поля «Название», легенда про звёздочку и кнопка. Заглушки правильной формы —
 * только у неизвестных полей схемы: короткая полоска-подпись + полоса в размер инпута.
 */
function ConnectFormSkeleton({ credType, edit }: { credType: string; edit?: boolean }) {
  const t = useTranslations('cabinet.connections');
  const locale = useLocale();
  const guide = useServiceGuide(credType, locale);
  const hintKey = serviceFor(credType)?.hintKey;
  const hint = guide ?? (hintKey ? t(hintKey) : null);
  const skField = (key: number) => (
    <FIn key={key} label={<Sk w={key % 2 ? 96 : 128} h={12} r="round" />}>
      <Sk w="100%" h={43} r="sm" />
    </FIn>
  );
  return (
    <FPane>
      {hint ? (
        <p className="hint-box">{hint}</p>
      ) : hint === null ? (
        // Текст гайда ещё едет ленивым чанком — заглушка в форме будущего блока-подсказки.
        <p className="hint-box">
          <Sk w="92%" h={12} r="round" /> <Sk w="70%" h={12} r="round" />
        </p>
      ) : null}
      {/* Порядок карточек — как у настоящей формы (П1 PAYMENTS-PLAN): данные сервиса
          первой карточкой, «Название» — отдельной второй. Название группы данных до
          загрузки схемы неизвестно — на его месте такая же полоска-заглушка. */}
      <FBlock label={<Sk w={104} h={12} r="round" />}>{[0, 1].map(skField)}</FBlock>
      <FBlock label={t('accountName')}>
        <Sk w="100%" h={43} r="sm" />
      </FBlock>
      <DFine>{t('requiredLegend')}</DFine>
      <DActions className="cf-acts">
        <KButton tone="primary" disabled>
          {edit ? t('saveAction') : t('connectAction')}
        </KButton>
      </DActions>
    </FPane>
  );
}

/**
 * Форма подключения по одному credType (C2 builder-ux: «Подключить <сервис>» из настроек
 * шага). Сервис ищется в общем каталоге аккаунтов (кэш+скелетон); нет в каталоге —
 * форма всё равно строится по схеме типа (имя — кураторское или из credType).
 */
export function ConnectCredTypeForm({
  credType,
  onDone,
  reload,
}: {
  credType: string;
  onDone: (id: string) => void;
  /** Освежить общий список доступов после успеха (стопка подключения из мастера: по
   *  нему мастер узнаёт НОВЫЙ аккаунт и ставит его в ответ). Не задан — как было. */
  reload?: () => void;
}) {
  const t = useTranslations('cabinet.connections');
  const locale = useLocale();
  const { data, error } = usePersistentData<CatalogData>(
    'cabinet:accounts:catalog',
    '/api/cabinet/accounts/catalog',
    { accept: (j) => !!(j as { ok?: boolean })?.ok },
  );
  if (!data) {
    return error ? <div className="empty">{t('catalogError')}</div> : <ConnectFormSkeleton credType={credType} />;
  }
  const entry = data.services.find((s) => s.credType === credType);
  const labelKey = entry?.generic ? GENERIC_LABEL_KEYS[credType] : undefined;
  const svc: PickerService = entry
    ? toPickerService(entry, locale, labelKey ? t(labelKey) : undefined)
    : { credType, name: svcName(serviceFor(credType), locale) ?? fallbackServiceName(credType) };
  return <ConnectForm svc={svc} reload={reload ?? (() => undefined)} onDone={onDone} />;
}

// Экспорт — переиспользуется карточкой-замочком в чате сценария (этап 4, Р4): та же
// секьюрная форма, тот же прямой путь web → инстанс, диалог и LLM секретов не видят.
// onDone (C2): вызывающий сам решает, что после успеха (drawer не закрывается здесь).
/** Робокасса, блок «Что вписать в кабинете Робокассы»: три адреса магазина с
 *  копированием и строка про алгоритм подписи. Клиент переносит это в технастройки
 *  магазина один раз, больше взять их неоткуда.
 *   • приём оплат (Result URL, сервер-к-серверу) — из /api/cabinet/products (payUrl);
 *   • возврат покупателя после оплаты и после отказа (Success/Fail URL) — собираются
 *     из введённого идентификатора магазина: в адресе, который видит ПОКУПАТЕЛЬ,
 *     нельзя светить userId (им же защищён вебхук-адрес /hook/<userId>/…).
 *  Идентификатор ещё не введён — блока нет вовсе, заглушек не рисуем. */
/** ОБЩИЙ блок «что вписать в кабинете кассы» (П1 PAYMENTS-PLAN: не рукописный на
 *  каждый сервис): подпись, адреса строкой с кнопкой «скопировать», сноска. Робокасса
 *  кормит его своими тремя адресами; приёмники ЮKassa/Stripe (П3/П4) добавят свои
 *  сюда же, а не собственную разметку. */
function PaySetupBlock({
  title,
  links,
  footnote,
}: {
  title: string;
  links: { label: string; url: string; hint: string | null }[];
  footnote?: string;
}) {
  const t = useTranslations('cabinet.connections');
  return (
    /* Название пузыря — «Адреса» у ВСЕХ касс (П5а); что и куда вписывать у конкретной
       кассы — строкой пояснения ВНУТРИ, сноска — подсказкой снизу. */
    <FBlock label={t('payAddrTitle')} {...(footnote ? { hint: footnote } : {})}>
      <FHint>{title}</FHint>
      {links.map((l) => (
        <MiniAppLinkField key={l.label} label={l.label} url={l.url} failed={false} hint={l.hint} qrHint={null} />
      ))}
    </FBlock>
  );
}

/** Адрес приёма оплат (`payUrl`) — из ручки «Продуктов». Раздел «Продукты» спрятан (hidden-features) — ручку не
 *  зовём вовсе и блока адресов не рисуем (ТЗ ~/projects/SPEED-TZ.md §5.2, П7 «скрытое от клиента не грузится»). */
const PRODUCTS_URL = SETTINGS_PRODUCTS_PEOPLE_HIDDEN ? '' : '/api/cabinet/products';

function RobokassaSetupBlock({ merchantLogin }: { merchantLogin: string }) {
  const t = useTranslations('cabinet.products');
  const tc = useTranslations('cabinet.connections');
  const { data } = usePersistentData<{ ok?: boolean; payUrl?: string }>(
    'cabinet:products',
    PRODUCTS_URL,
    { accept: (raw) => !!(raw as { ok?: boolean })?.ok },
  );
  if (SETTINGS_PRODUCTS_PEOPLE_HIDDEN) return null;
  const payUrl = data?.payUrl;
  if (!payUrl) return null;
  // Все поля формы видны сразу (П8 п.4): адрес приёма оплат не зависит от идентификатора
  // магазина и показывается с первого открытия; адреса возврата покупателя собираются из
  // идентификатора — до его ввода честная строка-подсказка вместо пустоты.
  const urls = robokassaSetupUrls(payUrl, merchantLogin);
  return (
    <PaySetupBlock
      title={t('rkSetupTitle')}
      links={[
        { label: t('payUrlTitle'), url: payUrl, hint: t('payUrlHint') },
        ...(urls
          ? [
              { label: t('backUrlTitle'), url: urls.successUrl, hint: t('backUrlHint') },
              { label: t('failUrlTitle'), url: urls.failUrl, hint: t('failUrlHint') },
            ]
          : []),
      ]}
      footnote={urls ? t('rkHashHint') : tc('rkAddrNeedLogin')}
    />
  );
}

/** «Название» — ПЕРВОЙ карточкой у всех форм подключения (владелец 2026-08-03, П8:
 *  «как можно название в самый низ» — прежний порядок П1 с названием после данных
 *  отменён). Один компонент на все ветки, чтобы порядок не расходился. */
function NameCard({
  name,
  setName,
  placeholder,
}: {
  name: string;
  setName: (v: string) => void;
  placeholder: string;
}) {
  const t = useTranslations('cabinet.connections');
  return (
    /* Единственное поле пузыря: имя пузыря И ЕСТЬ имя поля — FIn внутри не нужен
       (эталон «Кодовые слова» мастера). */
    <FBlock label={t('accountName')}>
      <KInput className="cf-inp" value={name} onChange={(e) => setName(e.target.value)} placeholder={placeholder} />
    </FBlock>
  );
}

/**
 * Поля мастера «Робокасса» (владелец 31.07): идентификатор магазина → блок «что вписать
 * в кабинете Робокассы» → два пароля и режим. Выбора алгоритма подписи нет (всегда
 * SHA256), Пароля №3 нет (он нужен только возвратам, которых у нас нет), служебные
 * поля схемы не показываем. Подписи паролей зависят от режима: у неактивированного
 * магазина пароли берутся из нижнего блока «Параметры проведения тестовых платежей»,
 * и человек должен это прочитать до, а не после «ошибки подписи».
 */
function RobokassaFields({
  name,
  setName,
  namePlaceholder,
  values,
  setValues,
  isTest,
  setIsTest,
  secretPh,
}: {
  name: string;
  setName: (v: string) => void;
  namePlaceholder: string;
  values: Record<string, string>;
  setValues: (fn: (v: Record<string, string>) => Record<string, string>) => void;
  isTest: boolean;
  setIsTest: (v: boolean) => void;
  /** Правка подключённого: пароль сохранён — поле пустое с плейсхолдером «сохранён». */
  secretPh?: (key: string) => string | undefined;
}) {
  const t = useTranslations('cabinet.connections');
  const tp = useTranslations('cabinet.products');
  const keys = robokassaPasswordKeys(isTest);
  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setValues((v) => ({ ...v, [key]: e.target.value }));
  const req = <span className="req" title={t('requiredField')}> *</span>;
  return (
    <>
      {/* ЕДИНЫЙ ПОРЯДОК со всеми подключениями (П8, владелец 2026-08-03): «Название»
          ПЕРВОЙ карточкой, затем данные сервиса (ключи и режим), чек, адреса для
          кабинета кассы. Тот же порядок в общей форме ниже. */}
      <NameCard name={name} setName={setName} placeholder={namePlaceholder} />
      {/* Казахстанская Робокасса (П9): тот же движок на robokassa.kz — по выбору
          узлы ходят на казахстанский шлюз; кабинет магазина там partner.robokassa.kz.
          Два варианта — раскрытым списком с галочкой отдельным пузырём, не выпадашкой. */}
      <FChoiceBlock
        label={t('rkRegion')}
        value={values.region === 'kz' ? 'kz' : 'ru'}
        onChange={(v) => setValues((m) => ({ ...m, region: v }))}
        options={[
          { value: 'ru', label: t('rkRegionRu') },
          { value: 'kz', label: t('rkRegionKz') },
        ]}
      />
      {/* Название пузыря (П5а): у касс поля данных подписаны «Ключи» — страница читается
          блоками, а не сплошным списком полей; у каждого поля внутри свой заголовок. */}
      <FBlock label={t('payKeysTitle')}>
        <FIn label={<>{tp('rkMerchant')}{req}</>}>
          <KInput className="cf-inp" value={values.merchantLogin ?? ''} onChange={set('merchantLogin')} placeholder="my-shop" />
        </FIn>
        <FIn label={<>{tp(keys.p1)}{req}</>}>
          <KInput className="cf-inp" type="password" value={values.password1 ?? ''} onChange={set('password1')} placeholder={secretPh?.('password1')} />
        </FIn>
        <FIn label={<>{tp(keys.p2)}{req}</>}>
          <KInput className="cf-inp" type="password" value={values.password2 ?? ''} onChange={set('password2')} placeholder={secretPh?.('password2')} />
        </FIn>
        {/* Режим магазина — тумблером строкой (имя жирным слева, переключатель справа,
            пояснение снизу): от него зависит, какие пароли просим. */}
        <KSwitch
          label={tp('rkTestMode')}
          checked={isTest}
          onChange={(v) => setIsTest(v)}
          {...(keys.note ? { hint: tp(keys.note) } : {})}
        />
      </FBlock>
      {values.region === 'kz' && (
        /* KZ (П9): у казахстанского шлюза алгоритм по умолчанию MD5, SHA256
           включает их поддержка по запросу — выбор честный, дефолт MD5. */
        <FChoiceBlock
          label={t('rkKzHash')}
          value={values.kzHashAlg === 'sha256' ? 'sha256' : 'md5'}
          onChange={(v) => setValues((m) => ({ ...m, kzHashAlg: v }))}
          options={[
            { value: 'md5', label: t('rkKzHashMd5') },
            { value: 'sha256', label: t('rkKzHashSha') },
          ]}
          hint={t('rkKzNote')}
        />
      )}
      {/* Чек 54-ФЗ (П2) — российская фискализация, у казахстанской кассы не применяется. */}
      {values.region !== 'kz' && (
        <PayReceiptFields credType="robokassaApi" values={values} setValues={setValues} />
      )}
      {/* Адреса магазина живут ЗДЕСЬ, не в «Продуктах» (MINIMAL-PRODUCT А2, владелец
          2026-07-29: настройка кассы — только в «Подключениях», в «Продуктах» — товары). */}
      <RobokassaSetupBlock merchantLogin={values.merchantLogin ?? ''} />
    </>
  );
}

/**
 * Чек 54-ФЗ кассы (PAYMENTS-PLAN П2): ставка НДС, СНО и признак способа расчёта.
 * Значения живут в НАШЕМ сейфе рядом с ключом (в кред n8n не уезжают), словари и
 * мапинг в коды кассы — @scriptera/ui pay-receipt (одно место). Ставка не выбрана —
 * чек не отправляется, платежи идут как раньше. Селекты — SmartSelect (канон форм).
 */
function PayReceiptFields({
  credType,
  values,
  setValues,
}: {
  credType: string;
  values: Record<string, string>;
  setValues: (fn: (v: Record<string, string>) => Record<string, string>) => void;
}) {
  const t = useTranslations('cabinet.connections');
  const opts = payReceiptOptionsFor(credType);
  if (!opts) return null;
  const set = (key: string) => (v: string) => setValues((m) => ({ ...m, [key]: v }));
  const select = (
    key: (typeof PAY_RECEIPT_KEYS)[number],
    labelKey: string,
    list: readonly string[],
    prefix: string,
    emptyLabel: string,
  ) => (
    // Списки длинные (у НДС их дюжина) — раскрытым списком строк они не помещаются:
    // канонный путь для такого выбора — SmartSelect в поле пузыря.
    <FIn label={t(labelKey)}>
      <SmartSelect
        value={values[key] ?? ''}
        onChange={set(key)}
        placeholder={emptyLabel}
        options={[
          { value: '', label: emptyLabel },
          ...list.map((v) => ({ value: v, label: t(`${prefix}${v}`) })),
        ]}
      />
    </FIn>
  );
  return (
    // Три поля одной темы — ОДИН пузырь с именем группы «Чек 54-ФЗ».
    <FBlock label={t('payReceiptTitle')}>
      {select('payVat', 'payReceiptVat', opts.vat, 'payVat_', t('payReceiptOff'))}
      {select('paySno', 'payReceiptSno', opts.sno, 'paySno_', t('payReceiptShopDefault'))}
      {select('payMode', 'payReceiptMode', opts.mode, 'payMode_', t('payReceiptShopDefault'))}
      <FHint>{t('payReceiptHint')}</FHint>
      {/* ЮKassa шлёт чек только на почту покупателя — без e-mail в шаге чека не будет. */}
      {credType === 'yooKassaApi' && <FHint>{t('payReceiptEmailHint')}</FHint>}
    </FBlock>
  );
}

/** Донастройка сервиса при подключении не подтвердилась (сервер, lib/connect-setup.ts):
 *  код беды → ключ текста. Кода нет в карте — форма закрывается как раньше (молча). */
const SETUP_WARN_KEYS: Record<string, string> = {
  tg_webhook: 'setupTgWebhook',
  stripe_webhook: 'setupStripeWebhook',
  rk_shop: 'setupRkShop',
  rk_password: 'setupRkPassword',
  rk_hash: 'setupRkHash',
  tk_password: 'setupTkPassword',
};

/** Платёжные сервисы, у которых уведомление о деньгах приходит НАМ вебхуком и адрес
 *  надо вписать в кабинете кассы. У Робокассы это делает свой блок (адреса зависят от
 *  магазина); у остальных касс из каталога уведомления не настраиваются — статус оплаты
 *  сценарий спрашивает у кассы сам, и это надо СКАЗАТЬ, а не молчать (владелец
 *  2026-08-03: «у одних есть колбэк-урл, у других нет — помойка»). */
const PAY_CRED_TYPES = new Set([
  'robokassaApi',
  'yooKassaApi',
  'tKassaApi',
  'cloudPaymentsApi',
  'prodamusApi',
  'stripeApi',
]);

/** ЮKassa: адрес приёмника уведомлений для «Интеграция → HTTP-уведомления» (П3).
 *  Адрес выводится из payUrl Робокассы (оба содержат userId, отдаёт сервер). */
function YooKassaSetupBlock() {
  const t = useTranslations('cabinet.connections');
  const { data } = usePersistentData<{ ok?: boolean; payUrl?: string }>(
    'cabinet:products',
    PRODUCTS_URL,
    { accept: (raw) => !!(raw as { ok?: boolean })?.ok },
  );
  if (SETTINGS_PRODUCTS_PEOPLE_HIDDEN) return null;
  const url = yooKassaNotifyUrl(data?.payUrl);
  if (!url) return null;
  return (
    <PaySetupBlock
      title={t('ykSetupTitle')}
      links={[{ label: t('ykNotifyTitle'), url, hint: t('ykNotifyHint') }]}
    />
  );
}

/** Stripe (П4 + П8 п.6): эндпоинт уведомлений кабинет заводит по API сам при
 *  подключении. Форма ОДИНАКОВА при добавлении и правке (П8 п.1) и всегда говорит
 *  правду про адреса:
 *   • добавление — блок «Адреса» с честной строкой «адрес добавим сами при сохранении»;
 *   • правка, приёмник заведён — тот же блок показывает вписанный адрес;
 *   • правка, приёмник не завёлся (ограниченный ключ) — адрес для «Developers →
 *     Webhooks» руками + поле ключа подписи whsec_ (уезжает в наш сейф, в кред n8n
 *     не попадает — сервер вынимает его до проверок). */
function StripeSetupBlock({
  edit,
  values,
  setValues,
}: {
  edit?: ConnectEdit;
  values: Record<string, string>;
  setValues: (fn: (v: Record<string, string>) => Record<string, string>) => void;
}) {
  const t = useTranslations('cabinet.connections');
  const { data } = usePersistentData<{ ok?: boolean; payUrl?: string }>(
    'cabinet:products',
    PRODUCTS_URL,
    { accept: (raw) => !!(raw as { ok?: boolean })?.ok },
  );
  if (SETTINGS_PRODUCTS_PEOPLE_HIDDEN) return null;
  const url = stripeNotifyUrl(data?.payUrl);
  if (!url) return null;
  if (!edit) {
    return (
      <FBlock label={t('payAddrTitle')}>
        <FHint>{t('stripeAutoNote')}</FHint>
      </FBlock>
    );
  }
  const configured =
    !!edit.saved?.values.webhookId || (edit.saved?.secretsSet ?? []).includes('webhookSecret');
  if (configured) {
    return (
      <PaySetupBlock
        title={t('stripeAutoDone')}
        links={[{ label: t('stripeNotifyTitle'), url, hint: null }]}
      />
    );
  }
  return (
    <>
      <PaySetupBlock
        title={t('stripeSetupTitle')}
        links={[{ label: t('stripeNotifyTitle'), url, hint: t('stripeNotifyHint') }]}
      />
      {/* Единственное поле пузыря — имя пузыря и есть имя поля, FIn не нужен. */}
      <FBlock label={t('stripeWhsecLabel')} hint={t('stripeWhsecHint')}>
        <KInput className="cf-inp"
          type="password"
          value={values.webhookSecret ?? ''}
          onChange={(e) => setValues((v) => ({ ...v, webhookSecret: e.target.value }))}
          placeholder="whsec_…"
        />
      </FBlock>
    </>
  );
}

/** Блок про уведомления кассы — одинаковый у ВСЕХ платёжных подключений: у касс с
 *  приёмником — адрес для их кабинета, у остальных — честное «адрес не нужен». */
function PayNotifyBlock({
  credType,
  edit,
  values,
  setValues,
}: {
  credType: string;
  edit?: ConnectEdit;
  values: Record<string, string>;
  setValues: (fn: (v: Record<string, string>) => Record<string, string>) => void;
}) {
  const t = useTranslations('cabinet.connections');
  if (!PAY_CRED_TYPES.has(credType) || credType === 'robokassaApi') return null;
  if (credType === 'yooKassaApi') return <YooKassaSetupBlock />;
  if (credType === 'stripeApi') {
    return <StripeSetupBlock edit={edit} values={values} setValues={setValues} />;
  }
  return <DHint>{t('payNoCallback')}</DHint>;
}

/**
 * Поля подключения «Email (SMTP)» (Ф6): данные почтового сервера клиента (ориентир —
 * Mailgun SMTP, поля универсальные для любого SMTP) + отправитель. Сырые поля схемы
 * n8n не показываем: подписи там английские, отправителя в схеме нет вовсе (он живёт
 * только в нашем сейфе), а служебный hostName человеку не нужен. Разбор и проверку
 * соединения (без единого письма) делает сервер — lib/smtp-conn.ts.
 */
function SmtpFields({
  name,
  setName,
  namePlaceholder,
  values,
  setValues,
  secretPh,
}: {
  name: string;
  setName: (v: string) => void;
  namePlaceholder: string;
  values: Record<string, string>;
  setValues: (fn: (v: Record<string, string>) => Record<string, string>) => void;
  /** Правка подключённого: пароль сохранён — поле пустое с плейсхолдером «сохранён». */
  secretPh?: (key: string) => string | undefined;
}) {
  const t = useTranslations('cabinet.connections');
  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setValues((v) => ({ ...v, [key]: e.target.value }));
  const req = <span className="req" title={t('requiredField')}> *</span>;
  return (
    <>
      {/* «Название» — первой карточкой (П8, единый порядок всех форм). */}
      <NameCard name={name} setName={setName} placeholder={namePlaceholder} />
      {/* Данные почтового сервера — ОДИН пузырь, у каждого поля свой заголовок. Имя
          группы — имя сервиса («Email (SMTP)»): своего словарного имени под эту группу
          в словаре нет, а безымянных пузырей набор не собирает. */}
      <FBlock label={namePlaceholder} hint={t('smtpSecureHint')}>
        <FIn label={<>{t('smtpHost')}{req}</>}>
          <KInput className="cf-inp" value={values.host ?? ''} onChange={set('host')} placeholder="smtp.mailgun.org" />
        </FIn>
        <FIn label={<>{t('smtpPort')}{req}</>}>
          <KInput className="cf-inp" value={values.port ?? ''} onChange={set('port')} inputMode="numeric" placeholder="587" />
        </FIn>
        <FIn label={<>{t('smtpLogin')}{req}</>}>
          <KInput className="cf-inp" value={values.user ?? ''} onChange={set('user')} placeholder="postmaster@mg.example.com" />
        </FIn>
        <FIn label={<>{t('smtpPassword')}{req}</>}>
          <KInput className="cf-inp" type="password" value={values.password ?? ''} onChange={set('password')} placeholder={secretPh?.('password')} />
        </FIn>
      </FBlock>
      {/* Отправитель — своя тема, свой пузырь. */}
      <FBlock label={t('handleSender')} hint={t('smtpFromHint')}>
        <FIn label={<>{t('smtpFrom')}{req}</>}>
          <KInput className="cf-inp" value={values.fromEmail ?? ''} onChange={set('fromEmail')} placeholder="hello@example.com" />
        </FIn>
        <FIn label={t('smtpFromName')}>
          <KInput className="cf-inp" value={values.fromName ?? ''} onChange={set('fromName')} placeholder={t('smtpFromNamePh')} />
        </FIn>
      </FBlock>
    </>
  );
}

/** Ответ /api/cabinet/accounts/schema — кэшируется общим стором (прогрев раздела). */
interface SchemaData {
  ok: boolean;
  schema?: JsonSchema;
  labels?: Record<string, string>;
  defaults?: Record<string, string>;
  oauth?: boolean;
  redirectUrl?: string;
  connectVia?: { provider: string; title: string };
}

/** Правка подключённого (владелец 2026-08-03, п.7): та же форма, поля предзаполнены
 *  сохранёнными значениями; секреты — пустые с плейсхолдером «сохранён», уезжают на
 *  сервер только если ввели новое. saved: null — сохранённых данных у нас нет (доступ
 *  заведён руками в редакторе) — поля пустые, пересохранение попросит ввести всё. */
export interface ConnectEdit {
  id: string;
  name: string;
  saved: { values: Record<string, string>; secretsSet: string[] } | null;
}

/** Чем кончилась запись подключения. Разбор ответа сервера — ОДИН на веб-форму и
 *  родную страницу подключения (волна 3): второй правды об исходах нет. */
export type ConnectSaveResult =
  | { ok: true; id: string; setupWarn?: string }
  | { ok: false; error: 'taken'; owner: string }
  // Pro и бот сверх оплаченных (ТЗ ACCOUNTS-TOGGLE §3.3): сначала докупка.
  | { ok: false; error: 'needPay'; paid: number; need: number }
  | { ok: false; error: 'takeoverFailed' | 'limit' | 'testFailed' | 'missing' | 'other'; message?: string };

/**
 * ЗАПИСЬ ПОДКЛЮЧЕНИЯ — ОДНОЙ РУКОЙ (волна 3, docs/NATIVE-TABS-PLAN.md): сбор данных
 * (мастер Робокассы, чек 54-ФЗ, правило «пустой секрет на правке = оставить
 * сохранённый») и разбор ответа сервера в понятный исход. Ею живут веб-форма
 * подключения и РОДНАЯ страница формы — второй записи доступа в кабинете нет.
 */
const CONNECT_SAVE_TIMEOUT_MS = 60_000;

export async function saveConnection(opts: {
  credType: string;
  /** Правка подключённого: id доступа. Пусто — новое подключение. */
  editId?: string;
  /** Готовое имя доступа (уже с запаской «имя сервиса»). */
  name: string;
  values: Record<string, string>;
  /** Мастер Робокассы собирает данные сам (алгоритм наш, Пароля №3 нет). */
  isRk?: boolean;
  rkTest?: boolean;
  /** Согласие забрать аккаунт у другого кабинета. */
  takeover?: boolean;
}): Promise<ConnectSaveResult> {
  const { credType, editId, name, values, isRk, rkTest, takeover } = opts;
  const rawBase = isRk
    ? robokassaCredData({
        merchantLogin: values.merchantLogin ?? '',
        password1: values.password1 ?? '',
        password2: values.password2 ?? '',
        isTest: !!rkTest,
        region: values.region,
        kzHashAlg: values.kzHashAlg,
      })
    : values;
  // Чек 54-ФЗ (П2): поля выбора едут всегда, включая пустые, — пустота на правке
  // означает «очистить настройку» (сервер кладёт их в сейф, в кред n8n не пускает).
  const raw: Record<string, string | boolean> = payReceiptOptionsFor(credType)
    ? { ...rawBase, ...Object.fromEntries(PAY_RECEIPT_KEYS.map((k) => [k, values[k] ?? ''])) }
    : rawBase;
  // Правка (владелец 2026-08-03, п.7): пустой секрет = «оставить сохранённый» — такие
  // ключи в PATCH не уезжают вовсе, сервер дольёт их из сейфа. Новое подключение шлёт
  // всё как есть (POST).
  const payload = editId
    ? Object.fromEntries(Object.entries(raw).filter(([k, v]) => !(isSecret(k) && (v == null || v === ''))))
    : raw;
  // Потолок ожидания: без него зависший на плохой сети запрос держал форму в «подключаем…»
  // бесконечно, и нажатия по «Подключить» уходили в пустоту (тупик c13c48c6, 15.10.2026).
  // Проверка подключения на сервере занимает секунды; минута — с запасом.
  const r = await fetch(editId ? accountUrl(editId) : '/api/cabinet/accounts', {
    method: editId ? 'PATCH' : 'POST',
    headers: { 'content-type': 'application/json' },
    signal: AbortSignal.timeout(CONNECT_SAVE_TIMEOUT_MS),
    body: JSON.stringify(
      editId
        ? { name, data: payload, ...(takeover ? { takeover: true } : {}) }
        : { name, type: credType, data: payload, ...(takeover ? { takeover: true } : {}) },
    ),
  })
    .then((res) => res.json())
    .catch(() => ({ ok: false }));
  if (r.ok) {
    const id = editId ?? (r.id ? String(r.id) : '');
    const warnKey = r.setup?.code ? SETUP_WARN_KEYS[String(r.setup.code)] : undefined;
    return warnKey ? { ok: true, id, setupWarn: String(r.setup.code) } : { ok: true, id };
  }
  if (r.error === 'account_taken') return { ok: false, error: 'taken', owner: typeof r.owner === 'string' ? r.owner : '' };
  if (r.error === 'takeover_failed') return { ok: false, error: 'takeoverFailed' };
  if (r.error === 'account_limit') return { ok: false, error: 'limit' };
  if (r.error === 'need_pay') return { ok: false, error: 'needPay', paid: Number(r.paid) || 0, need: Number(r.need) || 0 };
  if (r.error === 'test_failed') return { ok: false, error: 'testFailed', message: typeof r.message === 'string' ? r.message : undefined };
  // Сервер не пустил без обязательных полей (проверка в POST /api/cabinet/accounts, Ф24).
  if (r.error === 'missing_required') return { ok: false, error: 'missing' };
  return { ok: false, error: 'other' };
}

/**
 * СЛОВА ОТКАЗА — ОДНИ на веб-форму и родную страницу. Пояснение сервиса дописываем,
 * только если оно человеку что-то говорит: у многих узлов проверка возвращает
 * служебную английскую заглушку («failed - please check your credentials»), и клиент
 * читал «Ключ не прошёл проверку: failed - please check your credentials» — половина
 * фразы на чужом языке и без единой подсказки, что делать (Р8, сквозная приёмка).
 */
export function connectErrorText(
  res: ConnectSaveResult,
  t: (key: string) => string,
  edit: boolean,
): string {
  if (res.ok) return '';
  if (res.error === 'takeoverFailed') return t('takeoverFailed');
  if (res.error === 'testFailed') {
    return res.message && !isBoilerplateTestError(res.message)
      ? `${t('testFailed')}: ${res.message}`
      : t('testFailed');
  }
  if (res.error === 'missing') return t('missingRequired');
  return t(edit ? 'saveError' : 'connectError');
}

export function ConnectForm({
  svc,
  reload,
  onDone,
  edit,
  submitRef,
}: {
  svc: PickerService;
  reload: () => void;
  onDone?: (id: string) => void;
  edit?: ConnectEdit;
  /** Сохранение из ✓ в шапке модалки (владелец 2026-08-03, по образцу товаров).
   *  Задан — своей кнопки «Сохранить» форма не рисует; OAuth-ветки («Войти через…»,
   *  ожидание входа) остаются со своими кнопками — они не сохранение. */
  submitRef?: React.MutableRefObject<() => void>;
}) {
  const t = useTranslations('cabinet.connections');
  // Подписи полей мастера Робокассы — для списка незаполненного в проверке полноты.
  const tp = useTranslations('cabinet.products');
  const locale = useLocale();
  // close: в приложении снимает нативный слой формы, в вебе закрывает drawer.
  const drawer = useSlidePanel();
  // Схема полей — из общего кэша (прогрета входом в раздел): форма открывается без
  // скелетона, свежая схема доезжает тихо фоном.
  const { data: schemaData, error: schemaError } = usePersistentData<SchemaData>(
    schemaKey(svc.credType, locale),
    schemaUrl(svc.credType, locale),
    { accept: okAccept },
  );
  // Email (SMTP), Ф6 — форма-мастер (как Робокасса): свои подписи полей + отправитель
  // (его в схеме типа нет). Порт предзаполнен рекомендованным для Mailgun 587 (STARTTLS);
  // defaults каталога его не перекроют (свежий ввод в values главнее).
  const isSmtp = svc.credType === 'smtp';
  const [name, setName] = useState(edit?.name ?? '');
  // В состоянии — только то, что ЧЕЛОВЕК ввёл руками; показываемые значения собираются
  // деривацией ниже (дефолты каталога → сохранённое → свежий ввод), без setState в
  // эффектах (иммутабельность, и доехавшие фоном дефолты не трогают ввод).
  const [edited, setEdited] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Ф37.2: OAuth-тип (по extends-цепочке каталога, точнее эвристики по имени) + адрес
  // возврата для настроек приложения у сервиса; created — кред создан, идёт этап входа.
  // Робокасса — форма-мастер вместо сырых полей креда (владелец 31.07: «пусть будет как
  // мы скажем»): алгоритм подписи всегда SHA256 (выбора нет), Пароль №3 (возвраты) убран,
  // режим — одной галочкой «магазин ещё не активирован» (в креде это isTest).
  const isRk = svc.credType === 'robokassaApi';
  const [rkTest, setRkTest] = useState(edit?.saved?.values.isTest === 'true');
  const [createdId, setCreatedId] = useState<string | null>(null);
  // Донастройка сервиса не подтвердилась (приказ владельца 2026-08-04): подключение
  // сохранено и работает, но сервис не подтвердил обязательную настройку — честно
  // говорим человеку, что осталось, вместо молчаливого закрытия формы.
  const [setupWarn, setSetupWarn] = useState<string | null>(null);
  // Аккаунт занят другим кабинетом (ОДИН ВНЕШНИЙ АККАУНТ — ОДИН КАБИНЕТ, владелец
  // 2026-08-06): сервер вернул 409 с замаскированным владельцем — показываем
  // предупреждение ДО подключения и ждём явного согласия на передачу.
  const [takenBy, setTakenBy] = useState<string | null>(null);
  const [savedId, setSavedId] = useState('');
  // Аккаунтов больше не даёт тариф — вместо ошибки показываем экран с оплатой.
  const [limitHit, setLimitHit] = useState(false);
  const openBilling = useBillingDrawer();
  // ПОДКЛЮЧЕНИЕ СВЕРХ ОПЛАЧЕННЫХ (ТЗ ACCOUNTS-TOGGLE §3.3): в браузере — докупка «Добавить
  // аккаунты», после оплаты подключение повторяется само; в приложении оплаты нет — прежний
  // экран лимита со словами «на сайте» (запрет №7).
  const nativeApp = useNativeApp();
  const needPay = useNeedPayConnect();
  const tcn = useTranslations('cabinet.connections');
  const onNeedPay = (np: { paid: number; need: number }, retry: () => void) => {
    if (nativeApp) {
      setLimitHit(true);
      return;
    }
    setBusy(true);
    needPay(np, () => {
      setBusy(false);
      retry();
    }, () => {
      setBusy(false);
      setErr(tcn('notConnectedUnpaid'));
    });
  };
  // ТЕЛЕФОН В БРАУЗЕРЕ — ЛИСТ КАК РОДНОЙ (ТЗ WEB-PARITY-3 М60/М66, кадры iOS
  // W32-tg-step3-add-account / -token): значок 72 и заголовок в теле, «Название» строкой
  // «подпись · значение», поля сервиса строками «Токен доступа * ›» с листом ввода, белая
  // широкая «Подключить». Широкий экран и приложение — как были.
  const phoneWeb = usePhoneLook();
  // НОВЫЙ TELEGRAM — ЭКРАН ТОКЕНА ПО ТЗ USERTEST-1014 п.1 (владелец 14.10.2026), один вид с
  // онбордингом «Подключите аккаунты»: «Токен бота» → поле (без автофокуса и без листа ввода) →
  // пояснение → «Как получить токен» (шторка стопкой) → «Подключить». Слова — словарь онбординга.
  const tgNew = svc.credType === 'telegramApi' && !edit;
  const tob = useTranslations('onboarding.addAccount');
  const connectVia = useConnectVia(
    () => {
      reload();
      if (onDone) onDone('');
      else drawer.close();
    },
    (np, retry) => onNeedPay(np, retry),
  );
  // Вход подтверждён — сервис дал доступ, аккаунт готов к работе.
  const signIn = useOauthSignIn((id) => {
    reload();
    if (onDone) onDone(id);
    else drawer.close();
  });

  // Человеческие подписи полей из каталога/словаря («Токен доступа», «Идентификатор
  // магазина») вместо сырых имён полей («Access Token», «Base Url») — владелец 31.07.
  const labels = schemaData?.labels ?? {};
  const oauth = {
    on: schemaData ? schemaData.oauth === true : svc.oauth === true,
    redirectUrl: typeof schemaData?.redirectUrl === 'string' ? schemaData.redirectUrl : undefined,
  };
  // Ф4.2: тип закрыт OAuth-провайдером через НАШ домен → вместо формы ключей — одна
  // кнопка «Войти через …» (колбэк сам сохранит токен у нас и копию кредом в n8n).
  const via = schemaData?.connectVia ?? null;
  const schema: JsonSchema | null = schemaData ? (schemaData.schema ?? {}) : schemaError ? {} : null;

  // Показываемые значения: порт SMTP → дефолты каталога (например Base URL) →
  // сохранённое при подключении (правка) → свежий ввод. Ввод всегда главнее.
  const savedValues = edit?.saved?.values;
  const values = useMemo<Record<string, string>>(
    () => ({
      ...(isSmtp ? { port: '587' } : {}),
      ...(schemaData?.defaults ?? {}),
      ...(savedValues ?? {}),
      ...edited,
    }),
    [isSmtp, schemaData?.defaults, savedValues, edited],
  );
  // Тот же контракт, что раньше у setState формы: колбэк получает текущую карту и
  // возвращает новую — мастера Робокассы/SMTP пишут через него.
  const setValues = (fn: (v: Record<string, string>) => Record<string, string>) =>
    setEdited((prev) => fn({ ...prev }));

  // Секрет сохранён — поле пустое, плейсхолдер говорит об этом; ввели новое — уедет оно.
  const secretsSet = useMemo(() => new Set(edit?.saved?.secretsSet ?? []), [edit?.saved?.secretsSet]);
  const secretPh = (key: string) => (edit && secretsSet.has(key) ? t('secretSaved') : undefined);

  const fields = Object.keys(schema?.properties ?? {});
  const required = new Set(schema?.required ?? []);
  // Обязательные — в основном контейнере, необязательные — под «Дополнительно».
  // Схема не сказала, что обязательно (required пуст) → считаем обязательным ВСЁ:
  // спрятать нужное поле хуже, чем показать лишнее.
  const allRequired = required.size === 0;
  const requiredFields = allRequired ? fields : fields.filter((k) => required.has(k));
  const optionalFields = allRequired ? [] : fields.filter((k) => !required.has(k));

  const submit = async (takeover = false) => {
    setErr(null);
    setTakenBy(null);
    // Проверка полноты ДО отправки (П8 п.7, владелец 2026-08-03: «проверять, что всё
    // добавлено, при нажатии Ок — для всех сервисов»): пустые обязательные поля —
    // честный список по именам, а не общая фраза после похода на сервер. Пустой секрет
    // на правке обязательным не считается — сохранённый остаётся прежним.
    const emptySecret = (key: string) => !values[key]?.trim() && !(edit && secretsSet.has(key));
    const missing: string[] = [];
    if (isRk) {
      const keys = robokassaPasswordKeys(rkTest);
      if (!values.merchantLogin?.trim()) missing.push(tp('rkMerchant'));
      if (emptySecret('password1')) missing.push(tp(keys.p1));
      if (emptySecret('password2')) missing.push(tp(keys.p2));
    } else if (isSmtp) {
      if (!values.host?.trim()) missing.push(t('smtpHost'));
      if (!values.port?.trim()) missing.push(t('smtpPort'));
      if (!values.user?.trim()) missing.push(t('smtpLogin'));
      if (emptySecret('password')) missing.push(t('smtpPassword'));
      if (!values.fromEmail?.trim()) missing.push(t('smtpFrom'));
    } else {
      for (const k of requiredFields) {
        if (isSecret(k) ? emptySecret(k) : !values[k]?.trim()) missing.push(labels[k] ?? humanize(k));
      }
    }
    if (missing.length > 0) {
      setErr(missingRequiredText(missing, t));
      return;
    }
    setBusy(true);
    // Окно входа — сразу, пока мы внутри клика (после await блокировщик его не даст).
    const popup = oauth.on ? openSignInWindow() : null;
    const res = await saveConnection({
      credType: svc.credType,
      editId: edit?.id,
      name: name.trim() || svc.name,
      values,
      isRk,
      rkTest,
      takeover,
    });
    setBusy(false);
    if (res.ok) {
      reload();
      // Карточка подключённого держит сохранённые значения в своём кэше — освежаем его,
      // чтобы следующее открытие показало новые данные (тем же общим механизмом).
      if (edit) prefetchAccountData(edit.id, svc.credType, locale);
      // OAuth: кред создан/пересохранён, но доступа ещё нет — ведём через окно входа,
      // форма ждёт подтверждения (закроется сама, когда сервер поймает ответ провайдера).
      if (oauth.on && res.id) {
        setCreatedId(res.id);
        void signIn.start(res.id, popup);
        return;
      }
      popup?.close();
      // Сервис не подтвердил обязательную донастройку (вебхук, приёмник уведомлений,
      // магазин) — форму не закрываем: человек читает, что осталось сделать руками.
      // Сохранено оно уже точно, повторное нажатие ✓ в этой ветке только закрывает.
      if (res.setupWarn) {
        // id созданного доступа не теряем: закрытие экрана с предупреждением обязано
        // отдать его вызывающему так же, как обычное закрытие (шаг сценария по нему
        // выбирает только что подключённый аккаунт).
        setSavedId(res.id);
        setSetupWarn(res.setupWarn);
        return;
      }
      if (onDone) onDone(res.id);
      else drawer.close();
      return;
    }
    popup?.close();
    // Этот аккаунт уже подключён у другого пользователя: не подключаем молча — человек
    // читает, чем это кончится там, и решает сам (кнопка «Подключить здесь»).
    if (res.error === 'taken') {
      setTakenBy(res.owner);
      return;
    }
    // Лимит free/пробного периода: показываем объяснение с оплатой, а не строку ошибки.
    if (res.error === 'limit') {
      setLimitHit(true);
      return;
    }
    if (res.error === 'needPay') {
      onNeedPay(res, () => void submit(takeover));
      return;
    }
    setErr(connectErrorText(res, t, !!edit));
  };
  // §11.37 п.6: занят другим кабинетом — на телефоне в браузере вопрос герой-листом снизу.
  const takenRef = useRef<string | null>(null);
  useEffect(() => {
    if (!phoneWeb || takenBy === null || takenRef.current === takenBy) return;
    takenRef.current = takenBy;
    openHeroAsk(drawer, {
      icon: HERO_ICONS.persons,
      title: t('takenByHead'),
      text: t('takenBy', { owner: takenBy || t('takenByUnknown') }),
      actions: [{ key: 'takeover', label: t('takeoverAction'), tone: 'primary', onPick: () => void submit(true) }],
      closeLabel: t('takeoverCancel'),
      onCancel: () => {
        takenRef.current = null;
        setTakenBy(null);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [takenBy, phoneWeb]);


  // Инструкция «где взять ключ»: ПОШАГОВЫЙ гайд из service-guides главнее короткой
  // кураторской строки (владелец 2026-08-03: hintKey глушил гайд — Робокасса показывала
  // одну строку вместо шагов). Нет гайда — кураторский hintKey; нет ничего — без
  // подсказки. Показывать оба не стали: у части сервисов тексты почти совпадают
  // (Telegram) и дублировались бы друг под другом.
  const hintKey = serviceFor(svc.credType)?.hintKey;
  const guide = useServiceGuide(svc.credType, locale);
  const hint = guide ?? (hintKey ? t(hintKey) : null);

  /** Закрыть форму после сохранения (выход ветки с предупреждением о донастройке). */
  const finish = () => {
    if (onDone) onDone(savedId);
    else drawer.close();
  };

  // ✓ шапки модалки зовёт submit (тот же приём, что у карточки товара). В ветках без
  // формы полей (схема не доехала, вход через провайдера, ожидание OAuth) — глушим:
  // там сохранять нечего, а слепой POST отправил бы пустые значения. В ветке
  // предупреждения о донастройке ✓ закрывает: подключение уже сохранено, второй POST
  // завёл бы дубль.
  if (submitRef) {
    submitRef.current = setupWarn
      ? finish
      : // Экран передачи аккаунта: ✓ шапки не подтверждает отбор — согласие даётся
        // только своей кнопкой, случайным нажатием чужой аккаунт не отключают.
        takenBy !== null || !schema || via || createdId
        ? () => {}
        : () => void submit();
  }

  // Аккаунт занят другим кабинетом — предупреждение ДО подключения: что именно
  // произойдёт, у кого (маска почты) и две кнопки. Отмена возвращает к форме.
  // §11.37 п.6 (владелец 31.10: «полушторкой снизу, с героем, красиво… и в веб-приложении»): телефон в браузере —
  // герой-лист (тот же, что «Удалить бота?»), не группа с кнопками; десктоп — как был.
  if (takenBy !== null && !phoneWeb) {
    return (
      <DGroup>
        <DHint>{t('takenBy', { owner: takenBy || t('takenByUnknown') })}</DHint>
        <DActions className="cf-acts">
          <KButton tone="primary" disabled={busy} onClick={() => void submit(true)}>
            {t('takeoverAction')}
          </KButton>
          <KButton disabled={busy} onClick={() => setTakenBy(null)}>
            {t('takeoverCancel')}
          </KButton>
        </DActions>
      </DGroup>
    );
  }

  // Подключение сохранено, но сервис не подтвердил обязательную донастройку — экран
  // с честным объяснением и одной кнопкой (образец ветки ожидания входа ниже).
  if (setupWarn) {
    return (
      <DGroup>
        <DHint>{t(SETUP_WARN_KEYS[setupWarn])}</DHint>
        <DActions className="cf-acts">
          <KButton tone="primary" onClick={finish}>
            {t('setupGotIt')}
          </KButton>
        </DActions>
      </DGroup>
    );
  }

  if (!schema) {
    // Схема ещё не доехала (обычно только при холодном кэше: вход в раздел греет схемы
    // всех сервисов каталога) — каркас настоящей формы, не безликая полоса.
    return <ConnectFormSkeleton credType={svc.credType} edit={!!edit} />;
  }

  // Подпись поля — человеческая, из каталога/словаря (у наших РФ-сервисов сразу
  // русская). Нет в каталоге — прежняя сборка из имени поля.
  const fieldLabel = (key: string) => (
    <>
      {labels[key] ?? humanize(key)}
      {(allRequired || required.has(key)) && <span className="req" title={t('requiredField')}> *</span>}
    </>
  );
  const fieldInput = (key: string) => (
    <KInput className="cf-inp"
      type={isSecret(key) ? 'password' : 'text'}
      value={values[key] ?? ''}
      placeholder={isSecret(key) ? secretPh(key) : undefined}
      onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
    />
  );
  /** Обязательное поле — заголовком внутри общего пузыря данных сервиса. */
  const field = (key: string) => (
    <FIn key={key} label={fieldLabel(key)}>
      {fieldInput(key)}
    </FIn>
  );
  /** Поле из «Дополнительно» — своим пузырём: общего имени у разнородных необязательных
   *  настроек нет, а имя единственного поля пузырю подходит (FIn при этом не нужен). */
  const fieldBlock = (key: string) => (
    <FBlock key={key} label={fieldLabel(key)}>
      {fieldInput(key)}
    </FBlock>
  );

  // ЛИМИТ АККАУНТОВ: не «нельзя», а объяснение и кнопка оплаты (владелец 2026-08-10).
  if (connectVia.state === 'limit' || limitHit) {
    return <AccountLimitNote onPay={openBilling} {...(phoneWeb ? { hero: <ConnectHero svc={svc} title={t('connectServiceTitle', { name: svc.name })} /> } : {})} />;
  }

  // Ф4.2: вход через наш домен — без формы ключей вовсе, одна кнопка.
  if (via) {
    // Страница УЖЕ подключённого аккаунта (владелец 2026-08-03, п.6): раньше и здесь
    // стояло приглашение «Вход откроется в отдельном окне…» — читалось как «вы попали
    // на страницу подключения». Честный текст: аккаунт подключён входом, при сбое —
    // войти заново тем же окном.
    if (edit) {
      return (
        <DGroup>
          {connectVia.state === 'fail' && <DError>{t('oauthFailed')}</DError>}
          <DHint>{t('viaEditNote', { title: via.title })}</DHint>
          <DActions className="cf-acts">
            <KButton
              onClick={() => void connectVia.start(via.provider, openConnectWindow())}
              disabled={connectVia.state === 'waiting'}
            >
              {connectVia.state === 'waiting' ? t('oauthWaitingShort') : t('signInAgain')}
            </KButton>
            {connectVia.state === 'waiting' && (
              <KButton onClick={connectVia.cancel}>{t('cancelSignIn')}</KButton>
            )}
          </DActions>
        </DGroup>
      );
    }
    // ЛИЧНЫЙ АККАУНТ INSTAGRAM (владелец 20.10.2026): Meta отказ не присылает — человек
    // возвращается ни с чем. До входа — строка о требовании, после возврата без подключения —
    // заметная подсказка, как переключить аккаунт. Тексты — cabinet.connections.igPro*.
    const igPro = via.provider === 'instagram';
    return (
      <DGroup>
        {connectVia.state === 'fail' && <DError>{t('oauthFailed')}</DError>}
        {err && <DError>{err}</DError>}
        {igPro && connectVia.state === 'cancelled' && (
          <DWarn>
            <b>{t('igProTitle')}</b>
            <br />
            {t('igProNote')}
          </DWarn>
        )}
        <DHint>
          {t('connectViaNote', { title: via.title })}
          {igPro ? ` ${t('igProFoot')}` : ''}
        </DHint>
        <DActions className="cf-acts">
          <KButton
            tone="primary"
            onClick={() => {
              setErr(null);
              void connectVia.start(via.provider, openConnectWindow());
            }}
            disabled={connectVia.state === 'waiting' || busy}
          >
            {connectVia.state === 'waiting' ? t('oauthWaitingShort') : t('connectViaAction', { title: via.title })}
          </KButton>
          {/* «Отменить вход» (владелец 29.09.2026): передумал — ожидание снимается без ошибки. */}
          {connectVia.state === 'waiting' && <KButton onClick={connectVia.cancel}>{t('cancelSignIn')}</KButton>}
        </DActions>
      </DGroup>
    );
  }

  // Этап входа (Ф37.2): кред создан, ждём подтверждения от сервиса в окне входа.
  if (createdId) {
    return (
      <DGroup>
        {signIn.state === 'fail' ? <DError>{t('oauthFailed')}</DError> : <DHint>{t('oauthWaiting')}</DHint>}
        <DActions className="cf-acts">
          <KButton
            tone="primary"
            onClick={() => void signIn.start(createdId, openSignInWindow())}
            disabled={signIn.state === 'waiting'}
          >
            {signIn.state === 'waiting' ? t('oauthWaitingShort') : t('oauthRetry')}
          </KButton>
        </DActions>
      </DGroup>
    );
  }

  return (
    /* Корень формы — FPane (владелец 2026-08-11): те же правила пузырей, что у мастера
       шаблона и «Старта» (kit.css, скоуп .fk-pane). */
    <FPane>
      {/* Ошибка — красным сразу под шапкой (стандарт модалок, владелец 2026-07-23). */}
      {err && <DError>{err}</DError>}
      {/* Рукодельный доступ (заведён в редакторе — его данных у нас нет): поля пустые,
          честно говорим почему и что делать (владелец 2026-08-03, п.6). */}
      {edit && !edit.saved && <DHint>{t('editNoSaved')}</DHint>}
      {hint && !phoneWeb && !tgNew && <p className="hint-box">{hint}</p>}
      {svc.generic && <DHint>{t('genericNameHint')}</DHint>}
      {oauth.on && <DHint>{t('oauthNote')}</DHint>}
      {oauth.on && oauth.redirectUrl && (
        <MiniAppLinkField
          label={t('oauthRedirectLabel')}
          url={oauth.redirectUrl}
          failed={false}
          hint={t('oauthRedirectHint')}
          qrHint={null}
        />
      )}
      {isRk ? (
        <RobokassaFields
          name={name}
          setName={setName}
          namePlaceholder={svc.name}
          values={values}
          setValues={setValues}
          isTest={rkTest}
          setIsTest={setRkTest}
          secretPh={secretPh}
        />
      ) : isSmtp ? (
        <SmtpFields
          name={name}
          setName={setName}
          namePlaceholder={svc.name}
          values={values}
          setValues={setValues}
          secretPh={secretPh}
        />
      ) : tgNew ? (
        <>
          {/* ПОЛЯ «НАЗВАНИЕ» У НОВОГО TELEGRAM НЕТ (владелец 14.10.2026: «тупое поле, убрать»; в онбординге его
              нет — одинаково): имя подключения — имя сервиса по умолчанию, логин бота подставит сервер. */}
          {phoneWeb ? <ConnectHero svc={svc} title={t('connectServiceTitle', { name: svc.name })} /> : null}
          <FBlock label={tob('tgField')} hint={withBotFatherLinks(tob('tgTokenWhat'))}>
            <KInput
              className="cf-inp"
              value={values.accessToken ?? ''}
              placeholder={tob('tgPlaceholder')}
              aria-label={tob('tgField')}
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              onChange={(e) => setValues((v) => ({ ...v, accessToken: e.target.value }))}
            />
          </FBlock>
          <DActions className="cf-acts">
            <KButton action onClick={() => drawer.open(tgTokenHowPayload(tob('tgHowTitle')))}>
              {tob('tgHowBtn')}
            </KButton>
          </DActions>
        </>
      ) : phoneWeb ? (
        <>
          {/* В правке героя у формы нет — его рисует карточка аккаунта (AccountDrawer). */}
          {edit ? null : <ConnectHero svc={svc} title={t('connectServiceTitle', { name: svc.name })} />}
          {/* «Название» — строка «подпись слева · ввод справа» (канон «Настроек» Apple). */}
          <KListGroup className="cf-rows">
            <KListRow
              className="cf-field-row"
              title={t('accountName')}
              control
              value={
                <KInput
                  value={name}
                  placeholder={svc.generic ? t('serviceNamePlaceholder') : svc.name}
                  aria-label={t('accountName')}
                  onChange={(e) => setName(e.target.value)}
                />
              }
            />
          </KListGroup>
          {/* Данные сервиса — строки с «›», ввод на своём листе (М66). У Телеграма
              заголовок «Бот» и сноска одной строкой — те же слова, что у родной страницы. */}
          <FBlock
            label={svc.credType === 'telegramApi' ? t('tgGroupBot') : PAY_CRED_TYPES.has(svc.credType) ? t('payKeysTitle') : svc.name}
            {...(svc.credType === 'telegramApi' ? { hint: t('tgTokenNote') } : {})}
          >
            <KListGroup>
              {requiredFields.map((k) => (
                <KListRow
                  key={k}
                  title={fieldLabel(k)}
                  chevron
                  value={values[k] ? (isSecret(k) ? '••••••' : values[k]) : ''}
                  onClick={() =>
                    drawer.open({
                      // Звёздочка обязательного поля — и в заголовке листа (кадр iOS
                      // W32-tg-step3-token: «Токен доступа *»).
                      title: `${labels[k] ?? humanize(k)}${allRequired || required.has(k) ? ' *' : ''}`,
                      sheet: true,
                      deck: true,
                                            winKey: 'conn-field',
                      body: (
                        <ConnFieldSheet
                          initial={values[k] ?? ''}
                          secret={isSecret(k)}
                          saveLabel={t('saveAction')}
                          onSave={(v) => {
                            setValues((cur) => ({ ...cur, [k]: v }));
                            drawer.back();
                          }}
                        />
                      ),
                    })
                  }
                />
              ))}
            </KListGroup>
          </FBlock>
          <PayReceiptFields credType={svc.credType} values={values} setValues={setValues} />
          {optionalFields.length > 0 && (
            <details className="adv">
              <summary>{t('advanced')}</summary>
              {optionalFields.map(fieldBlock)}
            </details>
          )}
          <PayNotifyBlock credType={svc.credType} edit={edit} values={values} setValues={setValues} />
        </>
      ) : (
        <>
          {/* ОДИН ПОРЯДОК У ВСЕХ ПОДКЛЮЧЕНИЙ (П8, владелец 2026-08-03): «Название» —
              ПЕРВОЙ карточкой, данные сервиса — второй, «Дополнительно» — ниже. */}
          <NameCard
            name={name}
            setName={setName}
            placeholder={svc.generic ? t('serviceNamePlaceholder') : svc.name}
          />
          {/* Данные сервиса — ОДИН пузырь, у каждого поля свой заголовок. Имя пузыря:
              у касс «Ключи» (П5а, как у мастера Робокассы), у остальных — имя сервиса
              (своего словарного имени под эту группу нет, а безымянных пузырей набор
              не собирает). */}
          <FBlock label={PAY_CRED_TYPES.has(svc.credType) ? t('payKeysTitle') : svc.name}>
            {requiredFields.map(field)}
          </FBlock>
          {/* Чек 54-ФЗ платёжной кассы (П2) — сразу после ключей;
              у неплатёжных типов блок сам не рисуется (payReceiptOptionsFor = null). */}
          <PayReceiptFields credType={svc.credType} values={values} setValues={setValues} />
          {optionalFields.length > 0 && (
            <details className="adv">
              <summary>{t('advanced')}</summary>
              {optionalFields.map(fieldBlock)}
            </details>
          )}
          {/* Уведомления кассы: у сервисов с приёмником — адрес для их кабинета; у
              остальных объясняем, что адрес настраивать не нужно (владелец 2026-08-03:
              «у других вообще нет колбек-урл»). */}
          <PayNotifyBlock credType={svc.credType} edit={edit} values={values} setValues={setValues} />
        </>
      )}
      {/* У нового Telegram звёздочек нет (одно поле «Токен бота»), а между пояснением и
          «Подключить» по ТЗ USERTEST-1014 п.1 стоит только «Как получить токен». */}
      {tgNew ? null : (
        <DFine>
          {/* SMTP: canTest в каталоге нет (тест наш, не n8n), но соединение проверяется. */}
          {t('requiredLegend')} {svc.canTest || isSmtp ? t('testEnabled') : t('testMissing')}
        </DFine>
      )}
      {/* Сохранение через ✓ шапки (submitRef) — своей кнопки в теле нет. */}
      {!submitRef && (
        <DActions className="cf-acts">
          {/* Обёрткой, а не submit напрямую: первым доводом у submit идёт согласие на
              передачу аккаунта, и событие клика прочиталось бы как «да». На телефоне в
              браузере — белая широкая капсула с розовым словом (кадр W32). Геометрия на всех
              ширинах — роль «кнопка действия» (ТЗ WEB-CAUSES М33), тон по месту. */}
          <KButton tone="primary" action onClick={() => void submit()} disabled={busy}>
            {busy
              ? edit
                ? t('saving')
                : t('connecting')
              : edit
                ? t('saveAction')
                : oauth.on
                  ? t('connectAndSignIn')
                  : t('connectAction')}
          </KButton>
        </DActions>
      )}
      {/* «Дополнительно» нового Telegram (Base URL) — ПОД «Подключить»: порядок экрана токена по ТЗ USERTEST-1014
          п.1 («Токен бота» → поле → пояснение → «Как получить токен» → «Подключить») не ломается, блок остаётся. */}
      {tgNew && optionalFields.length > 0 && (
        <details className="adv">
          <summary>{t('advanced')}</summary>
          {optionalFields.map(fieldBlock)}
        </details>
      )}
    </FPane>
  );
}

// ─────────── Карточка аккаунта: та же форма с правкой + участие в сценариях ───────────

/** Ответ GET /api/cabinet/accounts/[id] (кэшируется общим стором, прогрет разделом). */
interface AccountInfo {
  ok: boolean;
  usage?: { id: string; name: string; active: boolean }[];
  usageKnown?: boolean;
  /** Сохранённые значения формы БЕЗ секретов + какие секреты сохранены (сейф). */
  form?: { values: Record<string, string>; secretsSet: string[] } | null;
}

/**
 * Страница подключённого (владелец 2026-08-03, п.7; порядок переставлен 29.08 —
 * «превратить в нормальный вид»).
 *
 * Сверху вниз, от «что это» к «что с ним делать»:
 *   1) шапка — логотип сервиса, ЛОГИН заголовком, сервис и состояние подписью;
 *   2) вход (у OAuth) — выполнен ли и кнопка войти заново;
 *   3) ГДЕ РАБОТАЕТ — в каких ботах доступ занят: это первое, что спрашивают, глядя на
 *      подключение, и раньше оно лежало ПОД всей формой, ниже сгиба;
 *   4) настройки — та же форма, что при подключении (секреты пустые с плейсхолдером
 *      «сохранён»), сохранение — ✓ в шапке модалки;
 *   5) отключение — последним, с предупреждением о занятых ботах.
 * Данные — из кэшей, прогретых входом в раздел.
 */
/**
 * ТУМБЛЕР АККАУНТА — ОДНА РУКА НА ПЛИТКУ, КАРТОЧКУ И РОДНУЮ КАРТОЧКУ (ТЗ ACCOUNTS-TOGGLE §3.4,
 * владелец 13.10.2026: «ровно как боты»). Выключенный за неоплату не включается: та же плашка
 * оплаты, что у тумблера бота (useBillingPlate; в приложении — только слова, без оплаты),
 * тумблер остаётся «выкл». Ручное вкл/выкл — `POST /api/cabinet/accounts/<id>/switch {on}`;
 * сервер сам гасит/возвращает ботов аккаунта.
 */
export function useAccountSwitch(reload: () => void): (a: Account, on: boolean) => Promise<void> {
  const t = useTranslations('cabinet.connections');
  const panel = useSlidePanel();
  const plate = useBillingPlate((title, text) => {
    if (hasNativeNotice()) void showNativeNotice({ style: 'sheet', title, text, haptic: 'error' });
  });
  // УШЁЛ В ДРУГОЙ КАБИНЕТ (§3.7): не плашка оплаты, а объяснение — вернуть можно, подключив заново.
  const movedNote = () => {
    const text = t('movedNote');
    if (hasNativeNotice()) {
      void showNativeNotice({ style: 'sheet', title: t('offMoved'), text, haptic: 'error' });
      return;
    }
    panel.open({
      title: t('offMoved'),
      ...EDITOR_SHEET,
      body: (
        <FPane>
          <FHint>{text}</FHint>
        </FPane>
      ),
    });
  };
  return async (a, on) => {
    const sw = a.switch;
    if (!sw) return;
    if (on && sw.offReason === 'moved') {
      movedNote();
      return;
    }
    if (on && sw.offReason === 'unpaid') {
      plate();
      return;
    }
    const r = (await fetch(`${accountUrl(a.id)}/switch`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ on }),
    })
      .then((res) => res.json())
      .catch(() => null)) as { ok?: boolean; error?: string } | null;
    if (r && !r.ok && r.error === 'unpaid') plate();
    if (r && !r.ok && r.error === 'moved') movedNote();
    reload();
  };
}

/** Подпись-причина под выключенным аккаунтом: «Выключен» / «Выключен — не оплачен» (§3.4). */
export function useAccountOffHint(): (sw: AccountSwitch | null | undefined) => string | null {
  const t = useTranslations('cabinet.connections');
  return (sw) =>
    !sw || sw.state === 'on'
      ? null
      : sw.offReason === 'moved'
        ? t('offMoved')
        : sw.offReason === 'unpaid'
          ? t('offUnpaid')
          : t('offManual');
}

/** Первая строка карточки подключения — «Включён» с тумблером, как в окне бота (§3.4). */
function AccountSwitchRow({ account, reload }: { account: Account; reload: () => void }) {
  const t = useTranslations('cabinet.connections');
  const toggle = useAccountSwitch(reload);
  const offHint = useAccountOffHint();
  // Живое состояние — из общего списка доступов (тот же кэш, что у плиток): тело карточки
  // открыто со снимком `account`, а после тумблера список освежается.
  const { data: list } = usePersistentData<AccountsData>(ACCOUNTS_KEY, ACCOUNTS_URL, { accept: okAccept });
  const sw = list?.accounts.find((a) => a.id === account.id)?.switch ?? account.switch;
  // Ожидаемое положение до ответа сервера (тумблер не прыгает назад, пока идёт запрос).
  const [want, setWant] = useState<boolean | null>(null);
  if (!sw) return null;
  const on = want ?? sw.state === 'on';
  const hint = offHint(want === null ? sw : { ...sw, state: on ? 'on' : 'off' });
  return (
    <KListSection>
      <KListGroup>
        <KListRow
          control
          title={t('switchOn')}
          {...(hint ? { hint } : {})}
          value={
            <KSwitch
              checked={on}
              ariaLabel={t('switchOn')}
              onChange={(v) => {
                if (v && (sw.offReason === 'unpaid' || sw.offReason === 'moved')) {
                  void toggle({ ...account, switch: sw }, v);
                  return;
                }
                setWant(v);
                void toggle({ ...account, switch: sw }, v).finally(() => setWant(null));
              }}
            />
          }
        />
      </KListGroup>
    </KListSection>
  );
}

export function AccountDrawer({
  account,
  reload,
  onDone,
  submitRef,
}: {
  account: Account;
  reload: () => void;
  /** Вторая колонка десктопа: закрыть карточку после сохранения/удаления (шторки нет). */
  onDone?: () => void;
  /** Сохранение из ✓ в шапке модалки (владелец 2026-08-03, по образцу товаров):
   *  прокидывается форме внутри — та не рисует свою кнопку «Сохранить». */
  submitRef?: React.MutableRefObject<() => void>;
}) {
  const t = useTranslations('cabinet.connections');
  const tp = useTranslations('cabinet.products');
  const tn = useTranslations('onboarding.niche');
  const statsAcc = useStatsAccount(account, true);
  const locale = useLocale();
  // close: в приложении снимает нативный слой карточки, в вебе закрывает drawer.
  const drawer = useSlidePanel();
  const svc = serviceFor(account.type);
  const [armed, setArmed] = useState(false);
  // «О ВАС В ЭТОМ АККАУНТЕ» (О5.2 п.5): ниша, что предлагает и пол — свойства аккаунта
  // канала. У кассы и почты профиля нет — в сеть за этим не ходим вовсе.
  const nicheOn = nicheAskable(account.type);
  const { data: nicheData, reload: reloadNiche } = useNicheData(nicheOn);
  const nicheAcc = nicheOn ? nicheAccountOf(nicheData?.accounts, account.id) : null;
  const niche = useNicheDraft();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Участие в сценариях + сохранённые значения формы — общий кэш (прогрет разделом):
  // карточка открывается мгновенно, живой ответ сервера доезжает тихо фоном.
  const {
    data: info,
    error: infoError,
    errorStatus: infoStatus,
  } = usePersistentData<AccountInfo>(accountKey(account.id), accountUrl(account.id), {
    accept: okAccept,
  });
  const usage = info ? (info.usage ?? []) : null;
  // Ф37.2: у OAuth-аккаунта показываем, выполнен ли вход, и даём войти (заново) прямо
  // отсюда — аккаунт, созданный без входа, доводится до рабочего без редактора n8n.
  // Дешёвый фильтр по имени типа до запроса: не-OAuth аккаунтам статус не нужен
  // (сервер всё равно проверяет тип по каталогу — эвристика лишь экономит запрос).
  const isOauthType = /oauth2/i.test(account.type);
  const { data: oauthData, mutate: mutateOauth } = usePersistentData<{ ok: boolean; oauth?: boolean; connected?: boolean }>(
    accountOauthKey(account.id),
    isOauthType ? accountOauthUrl(account.id) : '',
    { accept: okAccept },
  );
  const oauthInfo = oauthData?.ok && oauthData.oauth === true ? { connected: oauthData.connected === true } : null;
  const signIn = useOauthSignIn(() => {
    mutateOauth({ ok: true, oauth: true, connected: true });
    reload();
  });

  // ✓ ШАПКИ — ОДНА КНОПКА НА ДВЕ ЗАПИСИ (О5.2 п.5): доступ уходит своим PATCH'ем, а ниша
  // отдельной ручкой — мешать её с кредом n8n нельзя. Форма доступа ставит свою руку сюда,
  // а карточка отдаёт наружу общую: сначала ниша (если её трогали), потом сам доступ.
  const formSubmit = useRef<() => void>(() => {});
  if (submitRef) {
    submitRef.current = () => {
      if (nicheAcc && niche.dirty) {
        void niche.save([nicheAcc]).then(() => reloadNiche());
      }
      formSubmit.current();
    };
  }

  const remove = async () => {
    setBusy(true);
    setErr(null);
    const r = await fetch(accountUrl(account.id), { method: 'DELETE' })
      .then((res) => res.json())
      .catch(() => ({ ok: false }));
    setBusy(false);
    if (r.ok) {
      // «Свой сервис» пропал — конструктор не должен предлагать мёртвый пункт.
      if (isCustomServiceCredType(account.type)) refreshCustomServices();
      if (onDone) onDone();
      else drawer.close();
      reload();
      return;
    }
    // Раньше отказ сервера молча оставлял аккаунт на месте — честно говорим (Ф29).
    setErr(t('deleteError'));
  };

  const count = usage?.length ?? 0;
  const names = (usage ?? []).map((u) => `«${u.name}»`).join(', ');
  // Где доступ используется: пока идёт живая проверка сервера клиента (она обходит все
  // сценарии и не быстрая), показываем то, что УЖЕ знаем из наших сценариев — вместо
  // пустого скелетона. Ответ сервера потом заменяет список: он и решает при удалении.
  const shown: { id: string; name: string; active: boolean | null }[] | null =
    usage ?? (account.usedBy?.length ? account.usedBy.map((name, i) => ({ id: `own-${i}`, name, active: null })) : null);

  // Форма правки — тот же ConnectForm, что при подключении (п.7). Сервис собираем из
  // общего каталога (тот же кэш, что у пикера — лишнего запроса нет), фолбэк — имя из
  // credType, как в списке.
  const { data: cat } = usePersistentData<CatalogData>(
    'cabinet:accounts:catalog',
    '/api/cabinet/accounts/catalog',
    { accept: okAccept },
  );
  const catEntry = cat?.services.find((s) => s.credType === account.type);
  const genericLabelKey = catEntry?.generic ? GENERIC_LABEL_KEYS[catEntry.credType] : undefined;
  const formSvc: PickerService = catEntry
    ? toPickerService(catEntry, locale, genericLabelKey ? t(genericLabelKey) : undefined)
    : {
        credType: account.type,
        name: svcName(svc, locale) ?? fallbackServiceName(account.type),
        oauth: svc?.oauth ?? /oauth2/i.test(account.type),
      };

  // «Свой сервис» (владелец 2026-08-03): правка — ТА ЖЕ форма описания запроса, что при
  // подключении (custom-service-form), а не сырые поля generic-типа. Узнаём по мете в
  // сейфе; generic-кред без меты (заведён прежним универсальным способом или руками в
  // редакторе) остаётся прежней формой по схеме типа.
  const isCustomSvc = isCustomServiceCredType(account.type) && info?.form?.values.serviceKind === 'custom';

  const serviceName = isCustomSvc ? t('customService') : (svc?.name ?? fallbackServiceName(account.type));
  /* Состояние кассы в шапке (П5а): «принимает оплату», когда в сейфе есть всё, что
     нужно нашим приёмникам и узлам; иначе «нужно настроить». Только у трёх живых касс
     (у них комплект проверяем); данные ещё не доехали — не гадаем, шапка без чипа. */
  const payStatus = ((): 'ok' | 'setup' | null => {
    if (!['robokassaApi', 'yooKassaApi', 'stripeApi'].includes(account.type)) return null;
    if (!info) return null;
    const f = info.form;
    if (!f) return 'setup'; // рукодельный кред из редактора: ключей в нашем сейфе нет
    const has = (k: string) => f.secretsSet.includes(k);
    if (account.type === 'robokassaApi') {
      return f.values.merchantLogin && has('password1') && has('password2') ? 'ok' : 'setup';
    }
    if (account.type === 'yooKassaApi') return f.values.shopId && has('secretKey') ? 'ok' : 'setup';
    // Stripe: ключ + заведённый приёмник уведомлений (авто-эндпоинт или вписанный whsec_).
    return has('secretKey') && (f.values.webhookId || has('webhookSecret')) ? 'ok' : 'setup';
  })();

  // ТЕЛЕФОН В БРАУЗЕРЕ (ТЗ WEB-PARITY-3 К22/К23, кадры iOS K12-connection-ig / -tg): герой
  // 120, «О вас в этом аккаунте» строками со значением справа, «Где это в сценариях» со
  // словом состояния текстом, «Аналитика ›» строкой, форма строками, внизу белая
  // «Удалить аккаунт» красным с подсказкой (вопрос — окном по центру).
  const phoneWeb = readPhoneLook();
  const removeAsk = async () => {
    const yes = await askConfirm({ question: t('deleteConfirmQ'), confirmLabel: t('deleteYes'), cancelLabel: t('cancel') });
    if (yes) void remove();
  };
  // АККАУНТ УШЁЛ В ДРУГОЙ КАБИНЕТ (ТЗ ACCOUNTS-TOGGLE §3.7): креда у кабинета нет — карточка из
  // снимка: лицо, «Включён» (выключен, причина) и «Отключить» (убрать плитку пометкой).
  if (account.id.startsWith('moved:')) {
    return (
      <div className={phoneWeb ? 'conn-acc-ios' : undefined}>
        {err && <DError>{err}</DError>}
        <DHero icon={<ServiceLogo credType={account.type} iconNode={catEntry?.iconNode} name={serviceName} />} title={account.handle || serviceName}>
          {account.handle ? serviceName : null}
        </DHero>
        <AccountSwitchRow account={account} reload={reload} />
        <KButton action={phoneWeb} tone="danger" {...(phoneWeb ? { className: 'prof-wide-btn conn-acc-del' } : { size: 'sm' as const })} disabled={busy} onClick={() => void removeAsk()}>
          {t('deleteAccount')}
        </KButton>
      </div>
    );
  }
  return (
    <div className={phoneWeb ? 'conn-acc-ios' : undefined}>
      {err && <DError>{err}</DError>}
      {/* Шапка карточки (П5а): логотип сервиса, название и опознаватель (логин бота,
          магазин, адрес портала); у касс — состояние. Канон-примитив DHero — своей
          разметки шапки не заводим. */}
      {/* ШАПКА: ЛОГИН, А НЕ ТРЕТИЙ ПОВТОР КАНАЛА (владелец 29.08). Что это за сервис,
          говорит логотип; заголовком идёт опознаватель — логин бота, аккаунт Instagram,
          магазин кассы. Опознавателя нет (SMTP, ключ руками) — заголовком имя сервиса,
          иначе шапка была бы безымянной. Подписью — сам сервис и состояние. */}
      <DHero
        icon={
          phoneWeb && nicheOn ? (
            <ConnHeroFace
              accountId={account.id}
              initial={(account.handle || serviceName).replace(/^@+/, '').charAt(0).toUpperCase()}
              badge={<ServiceLogo credType={account.type} iconNode={catEntry?.iconNode} name={serviceName} />}
            />
          ) : (
            <ServiceLogo credType={account.type} iconNode={catEntry?.iconNode} name={serviceName} />
          )
        }
        title={account.handle || serviceName}
      >
        <>
          {account.handle ? serviceName : null}
          {payStatus && (
            <>
              {account.handle ? ' ' : null}
              <span className={`tag ${payStatus === 'ok' ? 'ok' : 'warn'}`}>
                {t(payStatus === 'ok' ? 'payStatusOk' : 'payStatusSetup')}
              </span>
            </>
          )}
        </>
      </DHero>
      <AccountSwitchRow account={account} reload={reload} />
      {phoneWeb && nicheAcc ? (
        <KListSection label={tn('cardGroup')}>
          <NicheAccountFields acc={nicheAcc} data={nicheData} draft={niche.draft} onChange={niche.change} rows />
        </KListSection>
      ) : null}
      {/* Робокасса со старым алгоритмом подписи: молча переключать нельзя — в кассе у
          клиента остался прежний, подпись перестала бы сходиться (владелец 31.07);
          пересохранение формой ниже переводит доступ на SHA256. */}
      {account.type === 'robokassaApi' && account.legacyHash && <DHint>{tp('rkHashLegacy')}</DHint>}
      {oauthInfo && (
        <>
          <div className="kv">
            <span>{t('oauthStatusLabel')}</span>
            <b>
              <span className={`tag ${oauthInfo.connected ? 'ok' : 'warn'}`}>
                {oauthInfo.connected ? t('oauthConnected') : t('oauthNotConnected')}
              </span>
            </b>
          </div>
          <div style={{ margin: '10px 0 2px' }}>
            <KButton
              size="sm"
              className="cf-signin"
              onClick={() => void signIn.start(account.id, openSignInWindow())}
              disabled={signIn.state === 'waiting'}
            >
              {signIn.state === 'waiting'
                ? t('oauthWaitingShort')
                : oauthInfo.connected
                  ? t('signInAgain')
                  : t('signInAction')}
            </KButton>
            {signIn.state === 'waiting' && (
              <p className="meta" style={{ margin: '8px 0 0' }}>{t('oauthWaiting')}</p>
            )}
            {signIn.state === 'fail' && (
              <p className="meta" style={{ margin: '8px 0 0' }}>{t('oauthFailed')}</p>
            )}
          </div>
        </>
      )}

      {/* АНАЛИТИКА АККАУНТА (владелец 10.09.2026, п.16а): статистика Instagram или цифры
          телеграм-бота — шторкой из карточки; раздела «Аналитика» больше нет. */}
      {statsAcc && !phoneWeb ? (
        <div className="wiz-reps-acts">
          <KButton size="sm" onClick={() => openStatsSheet(drawer, statsAcc)}>
            {t('statsRow')}
          </KButton>
        </div>
      ) : null}
      {/* Название пузыря — подписью НАД ним из набора (DList label): своей подписи со
          своими числами по месту не заводят, она в продукте одна (владелец 16.08.2026). */}
      <DList label={usage === null && !shown ? t('usageLoading') : t('usageTitle', { count: shown?.length ?? 0 })}>
        {shown === null ? (
          <div className="row">
            <div className="nm">
              <Sk w={160} h={14} />
            </div>
          </div>
        ) : shown.length === 0 ? (
          <div className="empty">{t('usageNone')}</div>
        ) : (
          shown.map((u) => (
            <div className="row" key={u.id}>
              <div className="nm">{u.name}</div>
              {/* Пока идёт живая проверка на сервере клиента, чипа «работает/на паузе»
                  нет: имена уже известны из наших сценариев, состояние — ещё нет. */}
              {u.active === null ? null : (
                <div className="right">
                  <span className={`tag ${u.active ? 'ok' : 'warn'}`}>
                    {u.active ? t('scenarioActive') : t('scenarioPaused')}
                  </span>
                </div>
              )}
            </div>
          ))
        )}
      </DList>

      {statsAcc && phoneWeb ? (
        <KListGroup className="conn-acc-stats">
          <KListRow title={t('statsRow')} chevron onClick={() => openStatsSheet(drawer, statsAcc)} />
        </KListGroup>
      ) : null}
      {/* ТА ЖЕ форма, что при подключении: поля предзаполнены сохранённым (без
          секретов), пересохранение уходит PATCH'ем. Пока карточка грузится ПЕРВЫЙ раз
          (кэша ещё нет), форму не собираем — иначе она родилась бы пустой и не узнала
          сохранённые значения (state формы сеется при монтировании).
          Ответ не пришёл (владелец 2026-08-03, п.6): страница ОБЯЗАНА открываться для
          любого существующего подключения — при любой ошибке, кроме честного 404,
          собираем форму с пустыми полями (подсказка «введите заново» внутри);
          сообщение об ошибке — только когда подключения правда нет (404). */}
      {info || (infoError && infoStatus !== 404) ? (
        isCustomSvc ? (
          <CustomServiceForm
            key={account.id}
            reload={reload}
            edit={{ id: account.id, name: account.name, saved: info?.form ?? null, credType: account.type }}
            submitRef={formSubmit}
            onDone={() => {
              // Карточка держит сохранённые значения в кэше — освежаем к следующему открытию.
              prefetchAccountData(account.id, account.type, locale);
              if (onDone) onDone();
              else drawer.close();
            }}
          />
        ) : (
          <ConnectForm
            key={account.id}
            svc={formSvc}
            reload={reload}
            edit={{ id: account.id, name: account.name, saved: info?.form ?? null }}
            submitRef={formSubmit}
            onDone={() => {
              if (onDone) onDone();
              else drawer.close();
            }}
          />
        )
      ) : infoError ? (
        <div className="empty">{t('accGone')}</div>
      ) : (
        <ConnectFormSkeleton credType={account.type} edit />
      )}

      {/* «О ВАС В ЭТОМ АККАУНТЕ» (О5.2 п.5): три поля с подтверждённым человеком, а пока
          он не отвечал — с догадкой по его же профилю. Подпись объясняет, откуда это
          взялось, БЕЗ слов «модель» и «уверенность» — они админские (запрет №7). */}
      {nicheAcc && !phoneWeb ? (
        <FPane>
          <NicheAccountFields acc={nicheAcc} data={nicheData} draft={niche.draft} onChange={niche.change} />
          {/* Подпись «мы определили» — только когда определять было что (есть догадка). */}
          {!nicheAcc.chosen && nicheAcc.guess ? <FHint>{tn('cardHint')}</FHint> : null}
        </FPane>
      ) : null}

      {phoneWeb ? (
        <>
          <KButton action className="prof-wide-btn conn-acc-del" disabled={busy} onClick={() => void removeAsk()}>
            {t('deleteAccount')}
          </KButton>
          <FHint className="ct-ios-hint">{count > 0 ? t('deleteWarnUsed', { count, names }) : t('deleteWarnNone')}</FHint>
        </>
      ) : null}
      <div className={`dzone${armed ? ' armed' : ''}${phoneWeb ? ' dzone--off' : ''}`}>
        <KButton tone="danger" size="sm" onClick={() => setArmed(true)}>
          {t('deleteAccount')}
        </KButton>
        <span className="ar-d">
          {count > 0 ? t('deleteWarnUsed', { count, names }) : t('deleteWarnNone')}
        </span>
        <div className="confirm">
          <span className="q">{t('deleteConfirmQ')}</span>
          <KButton tone="danger" size="sm" onClick={remove} disabled={busy}>
            {t('deleteYes')}
          </KButton>
          <KButton size="sm" onClick={() => setArmed(false)}>
            {t('cancel')}
          </KButton>
        </div>
      </div>
    </div>
  );
}

// ─────────── РОДНЫЕ СТРАНИЦЫ ПОДКЛЮЧЕНИЯ (волна 3, docs/NATIVE-TABS-PLAN.md) ───────────
//
// Реестр веб-остатков насчитал тут пять веб-поверхностей: карточка подключённого
// доступа, каталог «Подключить», форма «Подключить Telegram» (это НЕ OAuth), окна
// «занят другим кабинетом» / «донастройка не подтвердилась» / «лимит аккаунтов» и
// правка «Своего сервиса». Все они, кроме страниц самих провайдеров (вход Instagram —
// решение владельца), становятся РОДНЫМИ страницами стопки.
//
// ЭТАЛОН — ВЕБ ОДИН В ОДИН: тот же порядок («Название» первой карточкой, данные
// сервиса второй, «Дополнительно» ниже), те же слова инструкции «где взять ключ», та
// же легенда про звёздочку и проверку ключа, те же три окна исходов, то же
// сохранение ✓ в шапке.
//
// ЧЕГО РОДНОГО НЕТ И ПОЧЕМУ: мастера КАСС и почты (Робокасса, ЮKassa, Stripe, SMTP —
// свои поля, чек 54-ФЗ, адреса приёма оплат с QR) и форма «Своего сервиса» (списки
// входов/выходов с добавлением строк и живой пробой запроса) остаются веб-шторками:
// это отдельные мастера, а не форма по схеме. Сегодня они и не открываются — каталог
// отдаёт только Телеграм и Инстаграм (INTEGRATIONS_TG_IG_ONLY), — но у РАНЕЕ
// заведённых доступов такая карточка есть, и она по-прежнему веб-шторка.

/** Умеет ли родная форма этот тип доступа (см. оговорку выше). */
export function nativeConnectSupported(credType: string): boolean {
  if (isCustomServiceCredType(credType)) return false;
  if (credType === 'robokassaApi' || credType === 'smtp') return false;
  return !payReceiptOptionsFor(credType) && !PAY_CRED_TYPES.has(credType);
}

/** Строки формы подключения для родной страницы + её ✓ в шапке. Один код на форму
 *  нового подключения и на форму правки внутри карточки доступа — как и в вебе. */
function useNativeConnectForm(opts: {
  svc: PickerService | null;
  edit?: ConnectEdit;
  reload: () => void;
  /** ПРИСТАВКА КЛЮЧЕЙ. Форм на экране две — подключение нового сервиса и правка внутри
   *  карточки доступа, — и живут они одновременно: без своей приставки они разбирали бы
   *  тапы и ввод друг друга. */
  ns: string;
  /** Форма закрылась (подключили/сохранили) — раздел снимает свою страницу. */
  onDone: (id: string) => void;
}): {
  groups: NativeAccountGroup[];
  actions: NativeAccountAction[];
  /** КРАСНАЯ СТРОКА ПОД ШАПКОЙ страницы (владелец 09.09.2026, ТЗ волны 2 «Подключения»
   *  п.5): вход у провайдера не удался — человек обязан это увидеть, а не гадать.
   *  Пусто — строки нет. */
  error?: string;
  tapRow: (key: string) => boolean;
  applySet: (key: string, value: string | boolean) => boolean;
} {
  const t = useTranslations('cabinet.connections');
  const tob = useTranslations('onboarding.addAccount');
  const locale = useLocale();
  const { svc, edit, reload, ns, onDone } = opts;
  // Сборка с родной шторкой «Как получить токен» (ТЗ USERTEST-1014 п.15) — хуком (капкан гидрации).
  const tgHowNative = useNativeMethod('tgTokenHowOpen');
  const credType = svc?.credType ?? '';
  const { data: schemaData, error: schemaError } = usePersistentData<SchemaData>(
    credType ? schemaKey(credType, locale) : '',
    credType ? schemaUrl(credType, locale) : '',
    { accept: okAccept },
  );
  const [name, setName] = useState('');
  const [edited, setEdited] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [takenBy, setTakenBy] = useState<string | null>(null);
  const [setupWarn, setSetupWarn] = useState<string | null>(null);
  const [savedId, setSavedId] = useState('');
  const [limitHit, setLimitHit] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(null);
  // СМЕНИЛСЯ СЕРВИС ИЛИ ДОСТУП — форма начинается заново. В вебе это делает
  // перемонтирование (`key={account.id}` у ConnectForm), а родная страница у вида одна
  // и живёт всё время: сбрасываем состояние прямо в рендере, как советует React для
  // «поправить состояние при смене входа».
  const target = `${credType}|${edit?.id ?? ''}`;
  const [seen, setSeen] = useState(target);
  if (seen !== target) {
    setSeen(target);
    setName('');
    setEdited({});
    setErr(null);
    setTakenBy(null);
    setSetupWarn(null);
    setLimitHit(false);
    setCreatedId(null);
  }
  // Лимит бесплатного тарифа: В ПРИЛОЖЕНИИ ОПЛАТУ НЕ ПРЕДЛАГАЕМ (CLAUDE.md п.7) —
  // только честная причина, теми же словами, что у веб-версии в приложении.
  const { data: billing } = usePersistentData<{ subscription?: { paidAccountsCount?: number } | null }>(
    'cabinet:billing-summary',
    limitHit ? '/api/billing/summary' : '',
    { accept: (j) => !!(j as { ok?: boolean })?.ok },
  );
  const connectVia = useConnectVia(() => {
    reload();
    onDone('');
  });
  const signIn = useOauthSignIn((id) => {
    reload();
    onDone(id);
  });

  const labels = schemaData?.labels ?? {};
  const oauthOn = schemaData ? schemaData.oauth === true : svc?.oauth === true;
  const redirectUrl = typeof schemaData?.redirectUrl === 'string' ? schemaData.redirectUrl : undefined;
  const via = schemaData?.connectVia ?? null;
  const schema: JsonSchema | null = schemaData ? (schemaData.schema ?? {}) : schemaError ? {} : null;
  const savedValues = edit?.saved?.values;
  const values = useMemo<Record<string, string>>(
    () => ({ ...(schemaData?.defaults ?? {}), ...(savedValues ?? {}), ...edited }),
    [schemaData?.defaults, savedValues, edited],
  );
  const secretsSet = useMemo(() => new Set(edit?.saved?.secretsSet ?? []), [edit?.saved?.secretsSet]);
  const fields = Object.keys(schema?.properties ?? {});
  const required = new Set(schema?.required ?? []);
  // Схема не сказала, что обязательно (required пуст) → считаем обязательным ВСЁ:
  // спрятать нужное поле хуже, чем показать лишнее.
  const allRequired = required.size === 0;
  const requiredFields = allRequired ? fields : fields.filter((k) => required.has(k));
  const optionalFields = allRequired ? [] : fields.filter((k) => !required.has(k));
  const guide = useServiceGuide(credType, locale);
  const hintKey = serviceFor(credType)?.hintKey;
  const guideText = guide ?? (hintKey ? t(hintKey) : null);

  const submit = (takeover = false) => {
    // Идёт отправка — повторное нажатие запрос не дублирует (кнопка на странице уже выключена).
    if (busy) return;
    // Сервиса в каталоге нет (каталог не доехал) — нажатие НЕ может быть молчаливым: пять тапов
    // по «Подключить» за две секунды без единого ответа веба (инцидент c13c48c6, 15.10.2026).
    if (!svc) {
      setErr(connectErrorText({ ok: false, error: 'other' }, t, !!edit));
      return;
    }
    setErr(null);
    setTakenBy(null);
    // Проверка полноты ДО отправки (П8 п.7): пустые обязательные поля — честный список
    // по именам, а не общая фраза после похода на сервер. Пустой секрет на правке
    // обязательным не считается — сохранённый остаётся прежним.
    const emptySecret = (key: string) => !values[key]?.trim() && !(edit && secretsSet.has(key));
    const missing = requiredFields
      .filter((k) => (isSecret(k) ? emptySecret(k) : !values[k]?.trim()))
      .map((k) => labels[k] ?? humanize(k));
    if (missing.length > 0) {
      setErr(missingRequiredText(missing, t));
      return;
    }
    setBusy(true);
    void (async () => {
      const res = await saveConnection({
        credType: svc.credType,
        editId: edit?.id,
        name: name.trim() || edit?.name || svc.name,
        values,
        takeover,
      });
      setBusy(false);
      if (res.ok) {
        reload();
        if (edit) prefetchAccountData(edit.id, svc.credType, locale);
        // OAuth: кред создан, доступа ещё нет — ведём через окно входа (страница
        // провайдера остаётся чужой, вся обвязка вокруг неё — наша).
        if (oauthOn && res.id) {
          setCreatedId(res.id);
          void signIn.start(res.id, openSignInWindow());
          return;
        }
        if (res.setupWarn) {
          setSavedId(res.id);
          setSetupWarn(res.setupWarn);
          return;
        }
        onDone(res.id);
        return;
      }
      if (res.error === 'taken') {
        setTakenBy(res.owner);
        return;
      }
      // Сверх оплаченных (ТЗ ACCOUNTS-TOGGLE §3.3): в приложении оплаты нет — тот же экран лимита
      // со словами «на сайте» (запрет №7).
      if (res.error === 'limit' || res.error === 'needPay') {
        setLimitHit(true);
        return;
      }
      setErr(connectErrorText(res, t, !!edit));
    })();
  };

  const note = (key: string, text: string, tone: 'hint' | 'danger' = 'hint'): NativeAccountGroup => ({
    key,
    tone,
    hint: text,
    fields: [],
  });

  // ── Окна исходов: те же три экрана, что у веб-формы ────────────────────────────────
  if (takenBy !== null) {
    // Аккаунт занят другим кабинетом — что произойдёт, у кого (маска почты) и две кнопки.
    return {
      groups: [
        note(`${ns}-taken`, t('takenBy', { owner: takenBy || t('takenByUnknown') })),
        {
          key: `${ns}-taken-act`,
          fields: [
            { key: `${ns}:takeover`, kind: 'button', title: t('takeoverAction'), tone: 'accent', disabled: busy },
            { key: `${ns}:takeoverNo`, kind: 'button', title: t('takeoverCancel'), disabled: busy },
          ],
        },
      ],
      actions: [],
      tapRow: (key) => {
        if (key === `${ns}:takeover`) {
          submit(true);
          return true;
        }
        if (key === `${ns}:takeoverNo`) {
          setTakenBy(null);
          return true;
        }
        return false;
      },
      applySet: () => false,
    };
  }
  if (setupWarn) {
    // Подключение сохранено, но сервис не подтвердил обязательную донастройку.
    return {
      groups: [
        note(`${ns}-setup`, t(SETUP_WARN_KEYS[setupWarn] ?? 'setupGotIt')),
        { key: `${ns}-setup-act`, fields: [{ key: `${ns}:setupOk`, kind: 'button', title: t('setupGotIt'), tone: 'accent' }] },
      ],
      actions: [],
      tapRow: (key) => {
        if (key !== `${ns}:setupOk`) return false;
        onDone(savedId);
        return true;
      },
      applySet: () => false,
    };
  }
  if (connectVia.state === 'limit' || limitHit) {
    const paid = (billing?.subscription?.paidAccountsCount ?? 0) > 0;
    return {
      groups: [note(`${ns}-limit`, t(paid ? 'limitPaidNative' : 'limitFreeNative'))],
      actions: [],
      tapRow: () => false,
      applySet: () => false,
    };
  }
  if (!schema) {
    // Схема ещё не доехала — пусто (страница покажет ожидание), как скелетон веб-формы.
    return { groups: [], actions: [], tapRow: () => false, applySet: () => false };
  }
  if (via) {
    // Вход через наш домен: формы ключей нет вовсе, одна кнопка. Сама страница
    // провайдера остаётся чужой — родной у нас вся обвязка до и после неё.
    //
    // ОДИН ПУЗЫРЬ-КНОПКА СО СНОСКОЙ ПОД НИМ (владелец 09.09.2026, ТЗ волны 2
    // «Подключения» п.5 и п.6): прежний абзац НАД кнопкой («Вход откроется в отдельном
    // окне…») по канону Apple — сноска своего пузыря, одной строкой. Слова разные:
    // подключаем — что произойдёт; карточка — почему кнопка вообще есть.
    // ЛИЧНЫЙ АККАУНТ INSTAGRAM (владелец 20.10.2026): вернулся без подключения — не красная
    // «вход не подтвердился», а янтарная подсказка про профессиональный аккаунт и как его
    // переключить; до входа — строка о требовании под кнопкой.
    const igPro = via.provider === 'instagram';
    const failed = connectVia.state === 'fail' || (connectVia.state === 'cancelled' && !igPro);
    const groups: NativeAccountGroup[] = [
      ...(igPro && connectVia.state === 'cancelled'
        ? [
            {
              key: `${ns}-via-pro`,
              label: t('igProTitle'),
              tone: 'warn' as const,
              hint: t('igProNote'),
              fields: [],
            },
          ]
        : []),
      {
        key: `${ns}-via-act`,
        hint: `${edit ? t('viaCardNote', { title: via.title }) : t('connectViaFoot', { title: via.title })}${igPro ? ` ${t('igProFoot')}` : ''}`,
        fields: [
          {
            key: `${ns}:via`,
            kind: 'button',
            title:
              connectVia.state === 'waiting'
                ? t('oauthWaitingShort')
                : edit
                  ? t('signInAgain')
                  : t('connectViaAction', { title: via.title }),
            tone: 'accent',
            disabled: connectVia.state === 'waiting',
          },
          // «Отменить вход» (владелец 29.09.2026): в приложении окно входа — настоящий
          // Safari, его закрытия веб не видит; без этой кнопки выход был только перезапуском.
          ...(connectVia.state === 'waiting'
            ? [{ key: `${ns}:viaCancel`, kind: 'button' as const, title: t('cancelSignIn') }]
            : []),
        ],
      },
    ];
    return {
      groups,
      // ОШИБКА ВХОДА ВИДНА ВСЕГДА (ТЗ п.5): и когда провайдер отказал, и когда человек
      // вернулся из окна ни с чем — родной красной строкой под шапкой.
      ...(failed ? { error: t('oauthFailed') } : {}),
      actions: [],
      tapRow: (key) => {
        if (key === `${ns}:viaCancel`) {
          connectVia.cancel();
          return true;
        }
        if (key !== `${ns}:via`) return false;
        void connectVia.start(via.provider, openConnectWindow());
        return true;
      },
      applySet: () => false,
    };
  }
  if (createdId) {
    // Этап входа: кред создан, ждём подтверждения от сервиса в окне входа.
    return {
      // Ошибка входа — красной строкой под шапкой (ТЗ п.5), а в пузыре остаётся
      // объяснение «что делать».
      ...(signIn.state === 'fail' ? { error: t('oauthFailed') } : {}),
      groups: [
        note(`${ns}-wait`, t('oauthWaiting')),
        {
          key: `${ns}-wait-act`,
          fields: [
            {
              key: `${ns}:retry`,
              kind: 'button',
              title: signIn.state === 'waiting' ? t('oauthWaitingShort') : t('oauthRetry'),
              tone: 'accent',
              disabled: signIn.state === 'waiting',
            },
          ],
        },
      ],
      actions: [],
      tapRow: (key) => {
        if (key !== `${ns}:retry`) return false;
        void signIn.start(createdId, openSignInWindow());
        return true;
      },
      applySet: () => false,
    };
  }

  // ── Обычная форма по схеме ─────────────────────────────────────────────────────────
  const fieldRow = (key: string): NativeAccountField => {
    const secret = isSecret(key);
    const label = labels[key] ?? humanize(key);
    return {
      key: `${ns}:f:${key}`,
      kind: 'text',
      // Звёздочка обязательного — тем же значком, что в вебе (легенда внизу формы).
      title: allRequired || required.has(key) ? `${label} *` : label,
      value: values[key] ?? '',
      // СЛОВА ОШИБКИ ПУСТОГО ОБЯЗАТЕЛЬНОГО ПОЛЯ для листа правки (ТЗ П155 §3.3): «Токен
      // доступа не заполнен». Сохранённый секрет на правке пустым не считается — там
      // обязательности нет, и слов не шлём.
      ...((allRequired || required.has(key)) && !(secret && edit && secretsSet.has(key))
        ? { emptyError: t('fieldEmpty', { field: label }) }
        : {}),
      ...(secret ? { secret: true } : {}),
      ...(secret && edit && secretsSet.has(key) ? { placeholder: t('secretSaved') } : {}),
      done: t('saveAction'),
    };
  };

  // ТЕЛЕГРАМ — ФОРМА ПО КАНОНУ РОДНЫХ ФОРМ (владелец 09.09.2026, ТЗ волны 2
  // «Подключения» п.4): «Название» правится НА МЕСТЕ, у пузыря токена короткий заголовок
  // «Бот» и сноска одной строкой вместо длинного абзаца-инструкции над формой.
  const tg = credType === 'telegramApi';
  const groups: NativeAccountGroup[] = [];
  if (err) groups.push(note(`${ns}-err`, err, 'danger'));
  // Рукодельный доступ (заведён в редакторе — его данных у нас нет): поля пустые,
  // честно говорим почему и что делать.
  if (edit && !edit.saved) groups.push(note(`${ns}-nosaved`, t('editNoSaved')));
  if (guideText && !tg) groups.push(note(`${ns}-guide`, guideText));
  if (svc?.generic) groups.push(note(`${ns}-generic`, t('genericNameHint')));
  if (oauthOn) groups.push(note(`${ns}-oauth`, t('oauthNote')));
  if (oauthOn && redirectUrl) {
    groups.push({
      key: `${ns}-redirect`,
      label: t('oauthRedirectLabel'),
      hint: t('oauthRedirectHint'),
      fields: [{ key: `${ns}:redirect`, kind: 'copy', title: t('oauthRedirectLabel'), value: redirectUrl }],
    });
  }
  // ОДИН ПОРЯДОК У ВСЕХ ПОДКЛЮЧЕНИЙ (П8): «Название» — ПЕРВОЙ карточкой, данные
  // сервиса — второй, «Дополнительно» — ниже. У НОВОГО TELEGRAM «Названия» нет (владелец 14.10.2026: «тупое
  // поле, убрать»; в онбординге его нет — одинаково).
  if (!(tg && !edit)) groups.push({
    key: `${ns}-name`,
    // Заголовка над пузырём нет: имя строки и есть заголовок поля (канон «Настроек»
    // Apple — подпись слева, ввод справа), второй раз его писать незачем.
    fields: [
      {
        key: `${ns}:name`,
        // ПРАВКА НА МЕСТЕ, БЕЗ ЛИСТА (ТЗ п.4): вид 'field' — подпись слева, ввод справа;
        // старая сборка такого вида не знает и рисует прежнюю строку со значением.
        kind: 'field',
        title: t('accountName'),
        value: name || edit?.name || '',
        placeholder: svc?.generic ? t('serviceNamePlaceholder') : (svc?.name ?? ''),
        done: t('saveAction'),
      },
    ],
  });
  if (tg && !edit) {
    // НОВЫЙ TELEGRAM ПО ТЗ USERTEST-1014 п.1 (владелец 14.10.2026): пузырь «Токен бота», поле
    // прямо в строке (вид 'field' — без листа ввода, клавиатура только по тапу) с подсказкой
    // «Вставьте токен», под ним пояснение про @BotFather. Слова — словарь онбординга, те же,
    // что у веб-экрана. Остальные обязательные поля Telegram (Base URL) заполнены по умолчанию.
    groups.push({
      key: `${ns}-data`,
      label: tob('tgField'),
      hint: tob('tgTokenWhat'),
      fields: [
        {
          key: `${ns}:f:accessToken`,
          kind: 'field',
          title: '',
          value: values.accessToken ?? '',
          placeholder: tob('tgPlaceholder'),
          emptyError: tob('errors.tgEmpty'),
          done: t('saveAction'),
        },
      ],
    });
    // «КАК ПОЛУЧИТЬ ТОКЕН ›» ПОД ПОЛЕМ (ТЗ USERTEST-1014 п.15): сборка с родной шторкой (метод tgTokenHowOpen,
    // 250+) открывает её; старая — следующую страницу стопкой с той же инструкцией (TG_HOW_PAGE).
    groups.push({
      key: `${ns}-how`,
      fields: [{ key: `${ns}:tgHow`, kind: 'nav', title: tob('tgHowBtn'), ...(tgHowNative ? {} : { page: TG_HOW_PAGE }) }],
    });
  } else {
    groups.push({
      key: `${ns}-data`,
      label: tg ? t('tgGroupBot') : (svc?.name ?? ''),
      ...(tg ? { hint: t('tgTokenNote') } : {}),
      fields: requiredFields.map(fieldRow),
    });
  }
  // У нового Telegram «Дополнительно» (Base URL) встаёт ПОД кнопкой «Подключить» (ниже) — порядок экрана по ТЗ
  // USERTEST-1014 п.1 не ломается.
  if (optionalFields.length > 0 && !(tg && !edit)) {
    groups.push({ key: `${ns}-adv`, label: t('advanced'), fields: optionalFields.map(fieldRow) });
  }
  if (!(tg && !edit)) {
    groups.push(
      note(`${ns}-legend`, `${t('requiredLegend')} ${svc?.canTest ? t('testEnabled') : t('testMissing')}`),
    );
  }
  if (busy) groups.push(note(`${ns}-busy`, edit ? t('saving') : t('connecting')));
  // КНОПКА «ПОДКЛЮЧИТЬ» — КАПСУЛОЙ ВНИЗУ (ТЗ «Подключения» п.11 и волна 2 п.4): шаг
  // подключения живёт внутри шторки, и главное действие у него стоит под формой, как у
  // родного мастера. У ПРАВКИ внутри карточки всё как было — ✓ в шапке (там это не
  // «подключить», а «сохранить», и кнопка в теле спорила бы с «Удалить» внизу).
  if (!edit) {
    groups.push({
      key: `${ns}-go`,
      fields: [{ key: `${ns}:go`, kind: 'button', title: t('connectAction'), tone: 'accent', disabled: busy }],
    });
  }
  if (tg && !edit && optionalFields.length > 0) {
    groups.push({ key: `${ns}-adv`, label: t('advanced'), fields: optionalFields.map(fieldRow) });
  }

  return {
    groups,
    // Сохранение правки — ✓ В ШАПКЕ (как у веб-модалки: своей кнопки в теле нет).
    actions: edit ? [{ key: `${ns}:go`, icon: 'checkmark', label: t('saveAction') }] : [],
    tapRow: (key) => {
      if (key === `${ns}:tgHow`) {
        // Родная шторка есть — открываем её; нет — страницу TG_HOW_PAGE натив уже открыл сам по `page`.
        if (!tgHowNative) return false;
        void nativeBridge()?.tgTokenHowOpen?.();
        return true;
      }
      if (key !== `${ns}:go`) return false;
      submit();
      return true;
    },
    applySet: (key, value) => {
      if (typeof value !== 'string') return false;
      if (key === `${ns}:name`) {
        setName(value);
        return true;
      }
      const m = new RegExp(`^${ns}:f:(.+)$`).exec(key);
      if (!m) return false;
      setEdited((prev) => ({ ...prev, [m[1]!]: value }));
      return true;
    },
  };
}

/** Страница «Как получить токен» стопкой из формы Telegram (ТЗ USERTEST-1014 п.15) — для сборок без родной
 *  шторки (метода tgTokenHowOpen нет). */
export const TG_HOW_PAGE = 'page:tg-how';

/** Ключи РОДНЫХ страниц раздела «Подключения» (волна 3): каталог, форма подключения и
 *  карточка доступа. Страница у ВИДА одна — какой именно сервис или доступ на ней, веб
 *  наливает содержимым (строка называет свою страницу сама). */
export const CONN_PAGES = {
  add: 'page:conn-add',
  svc: 'page:conn-svc',
  acc: 'page:conn-acc',
  /** Вопрос «расскажите о себе» сразу после подключения канала (О5.2 п.4). */
  niche: 'page:conn-niche',
} as const;

/** Приставка строк формы правки внутри карточки доступа (её ✓ карточка перехватывает). */
const ACC_FORM_NS = 'acf';

/**
 * РОДНАЯ СТРАНИЦА ВОПРОСА «РАССКАЖИТЕ О СЕБЕ» (О5.2 п.4) — то же, что веб-шторка
 * NicheAskSheet: три поля по только что подключённому аккаунту, «Готово» и «Позже».
 * Оба выхода возвращают в «Подключения». Портрет мог не доехать (воркер идёт секунды) —
 * поля тогда просто без предвыбора, ждать нечего.
 */
function useNativeNichePage(opts: { credId: string; open: boolean }): {
  page: NativeAccountPage;
  tapRow: (key: string) => boolean;
  applySet: (key: string, value: string | boolean) => boolean;
} {
  const t = useTranslations('onboarding.niche');
  const { credId, open } = opts;
  const { data, reload } = useNicheData(open);
  const lists = useNicheOptions(data);
  const niche = useNicheDraft();
  const [busy, setBusy] = useState(false);
  const ns = 'nq';
  const list = data?.accounts ?? [];
  // Вход через провайдера номера доступа не отдаёт — тогда берём аккаунт, про который
  // ещё не спрашивали (только что подключённый и есть).
  const acc = (credId ? list.find((a) => a.credId === credId) : undefined) ?? list.find((a) => !a.chosen && !a.skipped) ?? null;

  const close = () => {
    // Примеры и живые виджеты идут главной нишей из этой же ручки — освежаем её кэш.
    reload();
    // ШТОРКА ЗАКРЫВАЕТСЯ ЦЕЛИКОМ (ТЗ «Подключения» п.13): вопрос был последним её шагом,
    // возвращаться в каталог человеку незачем — снимаем первый шаг, а с ним и всё, что
    // над ним. В списке «Подключений» тем временем уже стоит новая строка.
    closeNativeSettingsPage(CONN_PAGES.add);
  };
  const done = () => {
    if (!acc) return close();
    setBusy(true);
    void niche.save([acc]).then(() => {
      setBusy(false);
      close();
    });
  };

  const groups: NativeAccountGroup[] = acc
    ? [
        {
          key: `${ns}-fields`,
          label: t('cardGroup'),
          hint: nicheGroupHint(acc, t),
          fields: nicheNativeFields({ ns, acc, draft: niche.draft, lists, t }),
        },
        {
          key: `${ns}-act`,
          fields: [
            // «Далее» + «Пропустить» всегда (§11.38 п.2, владелец 31.10: «возможность пропустить должна быть»).
            { key: `${ns}:done`, kind: 'button', title: t('action'), tone: 'accent', disabled: busy },
            { key: `${ns}:later`, kind: 'button', title: t('later') },
          ],
        },
      ]
    : [{ key: `${ns}-act`, fields: [{ key: `${ns}:later`, kind: 'button', title: t('action'), tone: 'accent' }] }];

  const page: NativeAccountPage = {
    key: CONN_PAGES.niche,
    title: t('askTitle'),
    // ГЕРОЯ У ШАГА ШТОРКИ НЕТ (ТЗ «Подключения» п.13): заголовок стоит в строке шапки,
    // как у остальных шагов.
    loading: open && !data,
    present: 'step',
    groups,
  };

  return {
    page,
    tapRow: (key) => {
      if (key === `${ns}:done`) {
        done();
        return true;
      }
      if (key === `${ns}:later`) {
        close();
        return true;
      }
      return false;
    },
    applySet: (key, value) => {
      if (!acc) return false;
      const next = nicheNativeSet(ns, acc, niche.draft, key, value);
      if (!next) return false;
      niche.change(acc, next);
      return true;
    },
  };
}

/** Ключ и адрес списка доступов — ОДНИ на весь кабинет: по ним живут веб-раздел
 *  «Подключения», родная страница свода и стопка подключения из мастера (общий стор
 *  usePersistentData — один запрос на всех). */
export const ACCOUNTS_KEY = 'cabinet:accounts';
export const ACCOUNTS_URL = '/api/cabinet/accounts';

export interface AccountsData {
  state: 'none' | 'provisioning' | 'frozen' | 'ready';
  available: boolean;
  scripteraConnected: boolean;
  accounts: Account[];
}

/**
 * ЧЕМ КОРМЯТСЯ РОДНЫЕ СТРАНИЦЫ ПОДКЛЮЧЕНИЯ — ОДНОЙ РУКОЙ (владелец 11.09.2026, ТЗ
 * «Добавить аккаунт из мастера» A3): список доступов, каталог сервисов и готовые
 * картинки логотипов. Раньше этот сбор жил внутри свода «Подключений»
 * (useNativeConnectionsPage), а стопке из мастера он нужен ровно такой же — поэтому
 * вынесен сюда, а не скопирован (правило «копий элемента не заводить»).
 *
 * `open` — стопка правда открыта: пока нет, в сеть не ходим (кэш кабинета показывается
 * как обычно, запросов не делается).
 */
/** Название сервиса подключённого доступа («Telegram», «Instagram», имя своего
 *  сервиса) — им подписан логотип и им же берётся буква-запаска. Одна рука на веб-список
 *  и на родные страницы. */
export function serviceNameOf(a: Account): string {
  return isCustomServiceCredType(a.type) ? a.name : (serviceFor(a.type)?.name ?? fallbackServiceName(a.type));
}

export function useConnectFeed(open: boolean): {
  data: AccountsData | null;
  error: boolean;
  accounts: Account[];
  reload: () => void;
  catalog: CatalogData | null;
  catalogError: boolean;
  logos: Record<string, NativeServiceLogo>;
} {
  const { data, error, reload } = usePersistentData<AccountsData>(ACCOUNTS_KEY, open ? ACCOUNTS_URL : '', {
    accept: okAccept,
  });
  // Каталог сервисов — ТОТ ЖЕ кэш, что у веб-раздела и пикера: он даёт узел-представитель,
  // по которому берётся логотип. Один запрос на всех.
  const { data: cat, error: catError } = usePersistentData<CatalogData>(
    'cabinet:accounts:catalog',
    open ? '/api/cabinet/accounts/catalog' : '',
    { accept: okAccept },
  );
  const accounts = data?.available ? data.accounts : [];
  const credIconNode = new Map((cat?.services ?? []).map((x) => [x.credType, x.iconNode]));
  // Картинки логотипов готовит веб (у сборки их нет) — той же рукой, что рисует
  // ServiceLogo на веб-плитке: второй правды о логотипе в кабинете нет.
  // Знаки нужны и списку доступов, и КАТАЛОГУ «Подключить» — просим их одним списком.
  const logos = useNativeServiceLogos([
    ...accounts.map((a) => ({ credType: a.type, iconNode: credIconNode.get(a.type), name: serviceNameOf(a) })),
    ...(cat?.services ?? []).map((x) => ({
      credType: x.credType,
      iconNode: x.iconNode,
      name: serviceFor(x.credType)?.name ?? fallbackServiceName(x.credType),
    })),
  ]);
  return {
    data: data ?? null,
    error: !!error,
    accounts,
    reload,
    catalog: cat ?? null,
    catalogError: !cat && !!catError,
    logos,
  };
}

/**
 * ТРИ РОДНЫЕ СТРАНИЦЫ ПОДКЛЮЧЕНИЯ (волна 3): каталог «Подключить аккаунт» плитками,
 * форма подключения выбранного сервиса и карточка подключённого доступа. Состав — у
 * веб-экранов один в один (ConnectPicker, ConnectForm, AccountDrawer).
 *
 * `stack` — что открыто прямо сейчас: карточка и форма ходят в сеть, только когда их
 * страница правда открыта (кэш кабинета при этом показывается как обычно).
 */
export function useNativeConnectPages(opts: {
  accounts: Account[];
  reload: () => void;
  stack: string[];
  /** Каталог доступов (тот же кэш, что у веб-раздела) — из него берутся сервисы и знаки. */
  catalog: CatalogData | null | undefined;
  /** Каталог не доехал вовсе (обрыв сети) — как в веб-пикере, честное сообщение. */
  catalogError?: boolean;
  /** Готовые картинки логотипов по credType (их рисует веб — см. useNativeServiceLogos). */
  logos: Record<string, { logo?: string; logoColor: string; letter?: string }>;
}): {
  pages: NativeAccountPage[];
  /** Открыли карточку доступа: налить в неё выбранного. */
  showAccount: (id: string) => void;
  tapRow: (key: string) => boolean;
  applySet: (key: string, value: string | boolean) => boolean;
} {
  const t = useTranslations('cabinet.connections');
  const { accounts, reload, stack, catalog, catalogError, logos } = opts;
  const [svcPick, setSvcPick] = useState<string>('');
  const [accId, setAccId] = useState<string>('');
  /** Доступ, про который спрашиваем на странице вопроса (только что подключённый). */
  const [nicheCred, setNicheCred] = useState<string>('');
  const { sections } = usePickerSections(catalog);
  const account = accounts.find((a) => a.id === accId) ?? null;
  const svcOpen = stack.includes(CONN_PAGES.svc);
  const accOpen = stack.includes(CONN_PAGES.acc);
  const nicheOpen = stack.includes(CONN_PAGES.niche);

  // ── Каталог «Подключить аккаунт»: те же секции и те же плитки, что у веб-пикера ─────
  // ДАТЧИК ПУСТОГО КАТАЛОГА (владелец 03.09.2026, скрин 00:32: страница «Подключить
  // аккаунт» без единой плитки при ответе сервера 200). Причина по коду не найдена —
  // пишем в журнал клиента, сколько сервисов прислал сервер (nat.feed conn-catalog ok:0),
  // и показываем честное слово вместо пустоты (правило «сначала телеметрия»).
  const catEmpty = !catalogError && !!catalog && sections.length === 0;
  const catEmptyNoted = useRef<CatalogData | null>(null);
  useEffect(() => {
    if (!catEmpty || !catalog || catEmptyNoted.current === catalog) return;
    catEmptyNoted.current = catalog;
    noteFeed('conn-catalog', false, { rows: catalog.services?.length ?? 0 });
  }, [catEmpty, catalog]);
  const catGroups: NativeAccountGroup[] = catalogError || catEmpty
    ? [{ key: 'cat-err', tone: 'hint', hint: t('catalogError'), fields: [] }]
    : catalog
    ? sections.map((section) => ({
        key: `cat:${section.title}`,
        label: section.title,
        // СТРОКАМИ, А НЕ ПЛИТКАМИ (ТЗ «Подключения» п.10): пузырь на группу каталога,
        // в строке — плитка логотипа, название и шеврон.
        fields: section.list.map((svc): NativeAccountField => {
          const logo = logos[svc.credType];
          return {
            key: `conn:svc:${svc.credType}`,
            kind: 'nav',
            page: CONN_PAGES.svc,
            title: svc.name,
            // «Скоро» (владелец 2026-08-03): плитка видна, но подключение не открыто.
            ...(svc.soon ? { hint: t('soon'), disabled: true } : {}),
            ...(logo?.logo ? { logo: logo.logo } : { letter: svc.name.charAt(0).toUpperCase() }),
            logoColor: logo?.logoColor ?? '',
          };
        }),
      }))
    // Каталог ещё едет — пустой пузырь той же высоты, без скачков содержимого.
    : [{ key: 'cat-wait', fields: [] }];
  const catalogPage: NativeAccountPage = {
    key: CONN_PAGES.add,
    // ПЕРВЫЙ ШАГ ОДНОЙ ШТОРКИ (ТЗ п.10): крестик слева, заголовок «Подключить», героя
    // у шагов нет вовсе — они живут внутри листа, а не отдельными страницами.
    title: t('connect'),
    loading: !catalog,
    present: 'flow',
    groups: catGroups,
  };

  // ── Форма подключения выбранного сервиса ───────────────────────────────────────────
  const pickedSvc = useMemo<PickerService | null>(() => {
    if (!svcPick) return null;
    const entry = catalog?.services.find((x) => x.credType === svcPick);
    const labelKey = entry?.generic ? GENERIC_LABEL_KEYS[entry.credType] : undefined;
    return entry
      ? toPickerService(entry, 'ru', labelKey ? t(labelKey) : undefined)
      : { credType: svcPick, name: fallbackServiceName(svcPick) };
  }, [svcPick, catalog, t]);
  const svcForm = useNativeConnectForm({
    // ФОРМА ЖИВЁТ, ПОКА СЕРВИС ВЫБРАН (хотфикс 15.10.2026): в мастере (шаг «подарок» → «Добавить бота»)
    // стопка веба теряла страницу формы сразу после открытия, натив форму показывал, а «Подключить»
    // молча уходил в `if (!svc) return` — клиентка жала 30 раз, на сервер не ушло ничего.
    svc: svcOpen || svcPick ? pickedSvc : null,
    reload,
    ns: 'cf',
    // УСПЕХ — НАЗАД В «ПОДКЛЮЧЕНИЯ» (Н46): снимаем каталог и форму над ним одним
    // движением (как «назад» пальцем с каталога). ПОДКЛЮЧИЛИ КАНАЛ (О5.2 п.4) — на их
    // место встаёт вопрос «расскажите о себе»: подмена одним движением, без кадра пустоты.
    onDone: (id) => {
      if (nicheAskable(svcPick)) {
        setNicheCred(id);
        // ВОПРОС «РАССКАЖИТЕ О СЕБЕ» — СЛЕДУЮЩИМ ШАГОМ ТОЙ ЖЕ ШТОРКИ (ТЗ п.13): страница
        // сервиса уступает ему место, а каталог под ними остаётся — шторка не закрывается
        // и не открывается заново (иначе лист уезжал бы и тут же вставал обратно).
        replaceNativeSettingsPages(CONN_PAGES.svc, CONN_PAGES.niche);
        return;
      }
      closeNativeSettingsPage(CONN_PAGES.add);
    },
  });
  const nicheQ = useNativeNichePage({ credId: nicheCred, open: nicheOpen });
  const svcLogo = svcPick ? logos[svcPick] : undefined;
  const svcPage: NativeAccountPage = {
    key: CONN_PAGES.svc,
    title: pickedSvc ? t('connectServiceTitle', { name: pickedSvc.name }) : t('connectTitle'),
    // Логотип сервиса стоит СВЕРХУ ПО ЦЕНТРУ И БЕЗ ПЛИТКИ (ТЗ п.11) — этим занят герой:
    // рисует его один рецепт эталона, чужой знак не перекрашиваем.
    hero: {
      icon: 'link',
      color: svcLogo?.logoColor ?? readRoleHex('--green', '#0fa3b1'),
      title: pickedSvc ? t('connectServiceTitle', { name: pickedSvc.name }) : t('connectTitle'),
      ...(svcLogo?.logo ? { logo: svcLogo.logo, logoColor: svcLogo.logoColor } : {}),
    },
    loading: svcForm.groups.length === 0,
    // ВТОРОЙ ШАГ ТОЙ ЖЕ ШТОРКИ (ТЗ п.11): едет боком внутри неё, слева «назад».
    present: 'step',
    // Вход у провайдера не удался — красной строкой под шапкой шага (ТЗ волны 2 п.5).
    ...(svcForm.error ? { error: svcForm.error } : {}),
    actions: svcForm.actions,
    groups: svcForm.groups,
  };

  // ── Карточка подключённого доступа ─────────────────────────────────────────────────
  // КАРТОЧКА ПОСЛЕ «✓» И ПОСЛЕ УДАЛЕНИЯ — НАЗАД В «ПОДКЛЮЧЕНИЯ» (Н46, владелец 03.09.2026,
  // запись экрана 00:30). Раньше здесь сразу гасился accId: страница оставалась открытой,
  // теряла аккаунт и показывала «такого подключения больше нет», а возврата не было.
  // Теперь страница снимается; прежний выбор (accId/svcPick) гасить не нужно: карточка и
  // форма ходят в сеть и показывают «нет такого» только при открытой странице (accOpen /
  // svcOpen), а следующий тап ставит свой выбор заново.
  const card = useNativeAccountCard({
    account,
    open: accOpen,
    reload,
    logos,
    onGone: () => closeNativeSettingsPage(CONN_PAGES.acc),
  });

  // «КАК ПОЛУЧИТЬ ТОКЕН» СТРАНИЦЕЙ СТОПКОЙ (ТЗ USERTEST-1014 п.15, сборки без tgTokenHowOpen): переключатель
  // «У меня нет бота / У меня уже есть бот» строками-галочками (сегментов у страниц 1.1 нет), шаги плашкой,
  // внизу «Открыть @BotFather». Слова — словарь онбординга (те же, что native.addacc.tgHow*).
  const [tgHowMode, setTgHowMode] = useState<'new' | 'have'>('new');
  const tob = useTranslations('onboarding.addAccount');
  const tgHowSteps = tob(tgHowMode === 'new' ? 'tgHowNoBotSteps' : 'tgHowHaveBotSteps')
    .replace(/\|/g, ' ')
    .split('\n')
    .filter((x) => x.trim())
    .map((x, i) => `${i + 1}. ${x}`)
    .join('\n');
  const tgHowPage: NativeAccountPage = {
    key: TG_HOW_PAGE,
    title: tob('tgHowTitle'),
    present: 'step',
    groups: [
      {
        key: 'tgh-mode',
        fields: [
          { key: 'tgh:new', kind: 'check', radio: true, title: tob('tgHowNoBot'), value: '', on: tgHowMode === 'new' },
          { key: 'tgh:have', kind: 'check', radio: true, title: tob('tgHowHaveBot'), value: '', on: tgHowMode === 'have' },
        ],
      },
      { key: 'tgh-steps', tone: 'hint', hint: tgHowSteps, fields: [] },
      { key: 'tgh-go', fields: [{ key: 'tgh:bf', kind: 'button', title: tob('tgBotFather'), tone: 'accent' }] },
    ],
  };

  const tapRow = (key: string): boolean => {
    if (key === 'tgh:bf') {
      // Telegram открывает система (Capacitor отдаёт адрес UIApplication.open), как у плиток-ссылок.
      window.open(BOT_FATHER_URL, '_blank');
      return true;
    }
    const svc = /^conn:svc:(.+)$/.exec(key);
    if (svc) {
      setSvcPick(svc[1]!);
      return false; // страницу натив уже открыл сам
    }
    if (svcForm.tapRow(key)) return true;
    if (nicheQ.tapRow(key)) return true;
    return card.tapRow(key);
  };

  const applySet = (key: string, value: string | boolean): boolean => {
    if ((key === 'tgh:new' || key === 'tgh:have') && value === true) {
      setTgHowMode(key === 'tgh:new' ? 'new' : 'have');
      return true;
    }
    return svcForm.applySet(key, value) || nicheQ.applySet(key, value) || card.applySet(key, value);
  };

  return {
    pages: [catalogPage, svcPage, nicheQ.page, card.page, tgHowPage],
    showAccount: (id) => setAccId(id),
    tapRow,
    applySet,
  };
}

/**
 * КАКОЙ АККАУНТ СТАТИСТИКИ СТОИТ ЗА ДОСТУПОМ (владелец 10.09.2026, BACKLOG «обход iOS
 * 10.09» п.16а: статистика соцсети живёт в карточке подключения). Свод аккаунтов —
 * тот же, что кормил прежнюю «Аналитику» (/api/cabinet/analytics-accounts): у телеграм-
 * бота его id и есть доступ, у инстаграма статистику ждёт РЯД подключения, и его ищем
 * по доступу (`credId`). Статистика есть только у этих двух типов; остальным — null.
 */
function useStatsAccount(account: Account | null, open: boolean): StripAccount | null {
  const wants = !!account && (account.type === 'telegramApi' || account.type === 'facebookGraphApi');
  const { data } = usePersistentData<{ ok: boolean; accounts: StripAccount[] }>(
    'cabinet:analytics-accounts',
    open && wants ? '/api/cabinet/analytics-accounts' : '',
    { accept: (j) => Array.isArray((j as { accounts?: unknown } | null)?.accounts) },
  );
  if (!account || !wants) return null;
  const list = data?.accounts ?? [];
  if (account.type === 'telegramApi') {
    const tg = list.find((a) => a.kind === 'tg' && a.id === account.id);
    // Свод ещё едет — страницу открываем по тому, что знаем: id доступа и логин.
    return tg ?? { kind: 'tg', id: account.id, name: account.handle || account.name, avatar: null };
  }
  return list.find((a) => a.kind === 'ig' && a.credId === account.id) ?? null;
}

/** Веб-шторка статистики аккаунта — та же, что открывалась из полосы аккаунтов. */
function openStatsSheet(panel: ReturnType<typeof useSlidePanel>, acc: StripAccount): void {
  // Телефон в браузере (ТЗ WEB-PARITY-3 К20/К21, кадр iOS K12-connection-analytics): шапка
  // без заголовка, в теле герой — круг 88 (фото / знак сети / «@») и имя 22; стопка.
  const phone = readPhoneLook();
  const body = acc.kind === 'ig' ? <InstagramStats fixed={acc.id} /> : <TelegramBotStats botId={acc.id} />;
  panel.open({
    title: phone ? '' : acc.name,
    sheet: true,
    ...(phone ? {} : {}),
    body: phone ? (
      <div className="conn-stats-ios">
        <DHero
          icon={
            // Фото профиля Instagram кругом 88 (кадр iOS K12-connection-ig-analytics); нет фото —
            // знак сети; у Телеграма — «@» (K12-connection-analytics).
            acc.kind === 'ig' && acc.avatar ? (
              // eslint-disable-next-line @next/next/no-img-element -- фото профиля из нашего бакета
              <img className="conn-stats-ph" src={acc.avatar} alt="" width={88} height={88} decoding="async" />
            ) : acc.kind === 'ig' ? (
              <ChannelIcon channel="instagram" size={56} />
            ) : (
              <span className="conn-stats-at">@</span>
            )
          }
          color={acc.kind === 'ig' ? channelHex('instagram') : channelHex('telegram')}
          title={acc.name}
        />
        {body}
      </div>
    ) : (
      body
    ),
  });
}

/**
 * РОДНАЯ КАРТОЧКА ПОДКЛЮЧЁННОГО ДОСТУПА — состав веб-страницы (AccountDrawer) сверху
 * вниз, от «что это» к «что с ним делать»:
 *   1) шапка — логотип сервиса, ЛОГИН заголовком, сервис и состояние подписью;
 *   2) вход (у OAuth) — выполнен ли и кнопка войти заново;
 *   3) ГДЕ РАБОТАЕТ — в каких ботах доступ занят;
 *   4) настройки — та же форма, что при подключении (секреты пустые с плейсхолдером
 *      «сохранён»), сохранение — ✓ в шапке;
 *   5) отключение — последним, с предупреждением о занятых ботах.
 */
function useNativeAccountCard(opts: {
  account: Account | null;
  open: boolean;
  reload: () => void;
  logos: Record<string, { logo?: string; logoColor: string; letter?: string }>;
  onGone: () => void;
}): {
  page: NativeAccountPage;
  tapRow: (key: string) => boolean;
  applySet: (key: string, value: string | boolean) => boolean;
} {
  const t = useTranslations('cabinet.connections');
  const tp = useTranslations('cabinet.products');
  const tc = useTranslations('cabinet');
  const tn = useTranslations('onboarding.niche');
  const locale = useLocale();
  const { account, open, reload, logos, onGone } = opts;
  const [armed, setArmed] = useState(false);
  // Тумблер аккаунта (ТЗ ACCOUNTS-TOGGLE §3.4): существующее поле моста `switch` + `hint`.
  const toggleAccount = useAccountSwitch(reload);
  const offHint = useAccountOffHint();
  // «О ВАС В ЭТОМ АККАУНТЕ» (О5.2 п.5) — те же три поля, что на веб-карточке.
  const nicheOn = nicheAskable(account?.type ?? '');
  // ФОТО АККАУНТА ГЕРОЕМ (владелец 09.09.2026, ТЗ волны 2 «Подключения» п.6): та же
  // ручка, что даёт лицо аккаунта «Сообщениям», и тот же конвейер картинок для натива.
  // Фото есть только у каналов (инстаграм-профиль, фотография бота); не взялось — герой
  // рисует буквенный круг. Клиент сам ничего не качает.
  const avatarUrl = account && nicheOn && open ? `/api/cabinet/accounts/${account.id}/avatar` : '';
  const avatars = useNativePhotos(avatarUrl ? [avatarUrl] : [], 240);
  const heroPhoto = avatarUrl ? avatars[avatarUrl] : undefined;
  const { data: nicheData, reload: reloadNiche } = useNicheData(open && nicheOn);
  const nicheAcc = account && nicheOn ? nicheAccountOf(nicheData?.accounts, account.id) : null;
  const niche = useNicheDraft();
  const nicheLists = useNicheOptions(nicheData);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const id = account?.id ?? '';
  const { data: info, error: infoError, errorStatus: infoStatus } = usePersistentData<AccountInfo>(
    id ? accountKey(id) : '',
    id && open ? accountUrl(id) : '',
    { accept: okAccept },
  );
  const isOauthType = /oauth2/i.test(account?.type ?? '');
  const { data: oauthData, mutate: mutateOauth } = usePersistentData<{ ok: boolean; oauth?: boolean; connected?: boolean }>(
    id ? accountOauthKey(id) : '',
    id && open && isOauthType ? accountOauthUrl(id) : '',
    { accept: okAccept },
  );
  const oauthInfo = oauthData?.ok && oauthData.oauth === true ? { connected: oauthData.connected === true } : null;
  const signIn = useOauthSignIn(() => {
    mutateOauth({ ok: true, oauth: true, connected: true });
    reload();
  });
  const { data: cat } = usePersistentData<CatalogData>(
    'cabinet:accounts:catalog',
    open ? '/api/cabinet/accounts/catalog' : '',
    { accept: okAccept },
  );
  const catEntry = account ? cat?.services.find((x) => x.credType === account.type) : undefined;
  const svc = account ? serviceFor(account.type) : undefined;
  const genericLabelKey = catEntry?.generic ? GENERIC_LABEL_KEYS[catEntry.credType] : undefined;
  const formSvc: PickerService | null = !account
    ? null
    : catEntry
      ? toPickerService(catEntry, locale, genericLabelKey ? t(genericLabelKey) : undefined)
      : {
          credType: account.type,
          name: svcName(svc, locale) ?? fallbackServiceName(account.type),
          oauth: svc?.oauth ?? /oauth2/i.test(account.type),
        };
  const form = useNativeConnectForm({
    svc: open && account ? formSvc : null,
    ...(account ? { edit: { id: account.id, name: account.name, saved: info?.form ?? null } } : {}),
    reload,
    ns: ACC_FORM_NS,
    onDone: () => onGone(),
  });

  const serviceName = account ? (svc?.name ?? fallbackServiceName(account.type)) : '';
  /* Состояние кассы в шапке (П5а): «принимает оплату», когда в сейфе есть всё, что
     нужно нашим приёмникам и узлам; иначе «нужно настроить». Только у трёх живых касс;
     данные ещё не доехали — не гадаем, чипа нет. */
  const payStatus = ((): 'ok' | 'setup' | null => {
    if (!account || !['robokassaApi', 'yooKassaApi', 'stripeApi'].includes(account.type)) return null;
    if (!info) return null;
    const f = info.form;
    if (!f) return 'setup'; // рукодельный кред из редактора: ключей в нашем сейфе нет
    const has = (k: string) => f.secretsSet.includes(k);
    if (account.type === 'robokassaApi') {
      return f.values.merchantLogin && has('password1') && has('password2') ? 'ok' : 'setup';
    }
    if (account.type === 'yooKassaApi') return f.values.shopId && has('secretKey') ? 'ok' : 'setup';
    return has('secretKey') && (f.values.webhookId || has('webhookSecret')) ? 'ok' : 'setup';
  })();

  const remove = () => {
    if (!account) return;
    // Предупреждение о занятых ботах человек уже прочитал под кнопкой — вторым нажатием
    // он подтверждает снятие (то же inline-подтверждение, что в вебе).
    if (!armed) {
      setArmed(true);
      return;
    }
    setBusy(true);
    setErr(null);
    void (async () => {
      const r = await fetch(accountUrl(account.id), { method: 'DELETE' })
        .then((res) => res.json())
        .catch(() => ({ ok: false }));
      setBusy(false);
      if (r.ok) {
        if (isCustomServiceCredType(account.type)) refreshCustomServices();
        reload();
        onGone();
        return;
      }
      // Раньше отказ сервера молча оставлял аккаунт на месте — честно говорим (Ф29).
      setErr(t('deleteError'));
    })();
  };

  const usage = info ? (info.usage ?? []) : null;
  // Где доступ используется: пока идёт живая проверка сервера клиента, показываем то,
  // что УЖЕ знаем из наших сценариев — вместо пустого ожидания.
  const shown: { id: string; name: string; active: boolean | null }[] | null =
    usage ??
    (account?.usedBy?.length ? account.usedBy.map((name, i) => ({ id: `own-${i}`, name, active: null })) : null);
  const count = usage?.length ?? 0;
  const names = (usage ?? []).map((u) => `«${u.name}»`).join(', ');

  const statsAcc = useStatsAccount(account, open);
  const statsPanel = useSlidePanel();
  const groups: NativeAccountGroup[] = [];
  if (err) groups.push({ key: 'ac-err', tone: 'danger', hint: err, fields: [] });
  if (!account) {
    // Доступа по ссылке в списке нет (удалили, чужая ссылка) — честное сообщение.
    if (open) groups.push({ key: 'ac-gone', tone: 'hint', hint: t('accGone'), fields: [] });
  } else {
    // «Включён» — ПЕРВОЙ СТРОКОЙ, как в окне бота (ТЗ ACCOUNTS-TOGGLE §3.4); только у тарифицируемых.
    if (account.switch) {
      const hint = offHint(account.switch);
      groups.push({
        key: 'ac-sw',
        fields: [
          {
            key: 'ac:sw',
            kind: 'switch',
            title: t('switchOn'),
            on: account.switch.state === 'on',
            ...(hint ? { hint } : {}),
          },
        ],
      });
    }
    // Робокасса со старым алгоритмом подписи: молча переключать нельзя (владелец 31.07).
    if (account.type === 'robokassaApi' && account.legacyHash) {
      groups.push({ key: 'ac-rk', tone: 'hint', hint: tp('rkHashLegacy'), fields: [] });
    }
    if (oauthInfo) {
      groups.push({
        key: 'ac-oauth',
        fields: [
          {
            key: 'ac:oauthState',
            kind: 'value',
            title: t('oauthStatusLabel'),
            value: oauthInfo.connected ? t('oauthConnected') : t('oauthNotConnected'),
          },
          {
            key: 'ac:signIn',
            kind: 'button',
            title:
              signIn.state === 'waiting'
                ? t('oauthWaitingShort')
                : oauthInfo.connected
                  ? t('signInAgain')
                  : t('signInAction'),
            disabled: signIn.state === 'waiting',
          },
        ],
        // Отказ входа сюда не пишем: он стоит красной строкой под шапкой страницы
        // (ТЗ волны 2 «Подключения» п.5) — второй раз то же слово не показываем.
        ...(signIn.state === 'waiting' ? { hint: t('oauthWaiting') } : {}),
      });
    }
    // «О ВАС В ЭТОМ АККАУНТЕ» — та же группа, что на веб-карточке: подтверждённое
    // человеком, а пока он не отвечал — догадка по его же профилю. Подпись объясняет,
    // откуда это взялось, без слов «модель» и «уверенность» (запрет №7).
    if (nicheAcc) {
      groups.push({
        key: 'ac-niche',
        label: tn('cardGroup'),
        // ОДНА СНОСКА ПОД ПУЗЫРЁМ вместо абзацев под каждой строкой (ТЗ волны 2 п.6):
        // строки короткие «что — значение ›», объяснение стоит один раз внизу.
        hint: tn('cardNote'),
        fields: nicheNativeFields({
          ns: 'acn', acc: nicheAcc, draft: niche.draft, lists: nicheLists, t: tn, short: true,
        }),
      });
    }
    // ГДЕ РАБОТАЕТ — своим пузырём с названием НАД ним (как DList label в вебе).
    groups.push({
      key: 'ac-usage',
      label: usage === null && !shown ? t('usageLoading') : t('usageTitle', { count: shown?.length ?? 0 }),
      ...(shown !== null && shown.length === 0 ? { hint: t('usageNone') } : {}),
      fields: (shown ?? []).map(
        (u): NativeAccountField => ({
          key: `ac:use:${u.id}`,
          kind: 'value',
          title: u.name,
          // Пока идёт живая проверка на сервере клиента, состояния ещё нет — как в вебе.
          ...(u.active === null ? {} : { value: u.active ? t('scenarioActive') : t('scenarioPaused') }),
        }),
      ),
    });
    // АНАЛИТИКА АККАУНТА — строкой карточки (владелец 10.09.2026, п.16а): статистика
    // Instagram или цифры телеграм-бота открываются отсюда, раздела «Аналитика» больше нет.
    // Свежая сборка — родная страница стопкой (anaPageOpen), старая — веб-шторка.
    if (statsAcc) {
      groups.push({
        key: 'ac-stats',
        fields: [{ key: 'ac:stats', kind: 'nav', title: t('statsRow') }],
      });
    }
    // ТА ЖЕ форма, что при подключении: поля предзаполнены сохранённым (без секретов).
    // Ответ не пришёл (владелец 2026-08-03, п.6): карточка ОБЯЗАНА открываться при
    // любой ошибке, кроме честного 404 — форма собирается с пустыми полями.
    if (info || (infoError && infoStatus !== 404)) groups.push(...form.groups);
    else if (infoError) groups.push({ key: 'ac-none', tone: 'hint', hint: t('accGone'), fields: [] });
    // ОТКЛЮЧЕНИЕ — последним, с предупреждением о занятых ботах (те же слова).
    groups.push({
      key: 'ac-del',
      hint: count > 0 ? t('deleteWarnUsed', { count, names }) : t('deleteWarnNone'),
      fields: [
        {
          key: 'ac:del',
          kind: 'button',
          title: armed ? t('deleteYes') : t('deleteAccount'),
          tone: 'danger',
          disabled: busy,
        },
        ...(armed
          ? [{ key: 'ac:delNo', kind: 'button' as const, title: tc('cancel'), disabled: busy }]
          : []),
      ],
    });
  }

  const logo = account ? logos[account.type] : undefined;
  const title = account ? account.handle || serviceName : t('account');
  // Буква круга-заглушки — из логина БЕЗ «@» (общее правило волны 2 П4).
  const initial = title.replace(/^@+/, '').charAt(0).toUpperCase();
  const page: NativeAccountPage = {
    key: CONN_PAGES.acc,
    // ШАПКА: ЛОГИН, А НЕ ТРЕТИЙ ПОВТОР КАНАЛА (владелец 29.08) — что за сервис, говорит
    // логотип; заголовком идёт опознаватель, подписью — сам сервис и состояние.
    title,
    hero: {
      icon: 'link',
      color: logo?.logoColor ?? readRoleHex('--green', '#0fa3b1'),
      title,
      // СЕРВИС И СОСТОЯНИЕ (ТЗ «Подключения» п.14). Поломка объясняется ЗДЕСЬ целиком:
      // в списке от неё осталась строка состояния под логином, а причину человек читает,
      // открыв карточку (те же слова health.*, что на веб-плитке).
      meta: [
        account?.handle ? serviceName : '',
        account?.health?.code ? t(`health.${account.health.code}`) : '',
        payStatus ? t(payStatus === 'ok' ? 'payStatusOk' : 'payStatusSetup') : '',
      ]
        .filter(Boolean)
        .join(' · '),
      // ГЕРОЙ КАНАЛА — ФОТО АККАУНТА КРУГОМ (владелец 09.09.2026, ТЗ волны 2 п.6): как у
      // контакта. Фото нет — буквенный круг. Значок сервиса при этом стоит маленькой
      // плашкой в углу фото (`photoBadge`), а не заменяет собой лицо. У остальных
      // сервисов (касса, CRM) лица не бывает — там герой остаётся логотипом, как был.
      ...(nicheOn
        ? {
            ...(heroPhoto ? { photo: heroPhoto } : { initial }),
            ...(logo?.logo ? { photoBadge: logo.logo } : {}),
          }
        : logo?.logo
          ? { logo: logo.logo, logoColor: logo.logoColor }
          : {}),
    },
    loading: !!account && !info && !infoError,
    // СТОПКОЙ (владелец 09.09.2026, волна 2 п.6 — отменяет горизонтальную подачу того же
    // дня): карточка аккаунта — КОНЕЧНЫЙ ОБЪЕКТ, а такие окна по словарю §11.0 поднимают
    // родителя назад колодой.
    present: 'deck',
    // Ошибка входа у провайдера — красной строкой под шапкой (ТЗ волны 2 п.5).
    ...(form.error ? { error: form.error } : signIn.state === 'fail' ? { error: t('oauthFailed') } : {}),
    actions: form.actions,
    groups,
  };

  const tapRow = (key: string): boolean => {
    if (key === 'ac:stats') {
      if (!statsAcc) return true;
      if (nativeAnaPageSupported()) openNativeAccountStats(statsAcc);
      else openStatsSheet(statsPanel, statsAcc);
      return true;
    }
    if (key === 'ac:del') {
      remove();
      return true;
    }
    if (key === 'ac:delNo') {
      setArmed(false);
      return true;
    }
    if (key === 'ac:signIn') {
      if (account) void signIn.start(account.id, openSignInWindow());
      return true;
    }
    // ✓ ШАПКИ — ОДНА КНОПКА НА ДВЕ ЗАПИСИ (как в вебе): ниша уходит своей ручкой, доступ
    // своим PATCH'ем. Дальше строка идёт форме — она и закроет карточку.
    if (key === `${ACC_FORM_NS}:go` && nicheAcc && niche.dirty) {
      void niche.save([nicheAcc]).then(() => reloadNiche());
    }
    return form.tapRow(key);
  };

  const applySet = (key: string, value: string | boolean): boolean => {
    if (key === 'ac:sw') {
      if (account) void toggleAccount(account, value === true || value === '1' || value === 'true');
      return true;
    }
    if (nicheAcc) {
      const next = nicheNativeSet('acn', nicheAcc, niche.draft, key, value);
      if (next) {
        niche.change(nicheAcc, next);
        return true;
      }
    }
    return form.applySet(key, value);
  };

  return { page, tapRow, applySet };
}
